import { useEffect, useMemo, useState, useRef } from "react";
import Layout from "@/components/Layout";
import api, { apiErr, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { FileText, Sparkles, Loader2, Trash2, ArrowLeft, ArrowRight, Save, Send, Users, ClipboardCheck, CalendarClock, Plus, CheckCircle2, Printer, RefreshCw, Shapes, ImagePlus, X, Scissors, PenTool, MessageCircle, Info, Link as LinkIcon, ChevronUp, ChevronDown } from "lucide-react";
import { AiServiceNotice } from "@/components/AiServiceNotice";
import { MathInline, mathToHtmlPdf, hasMath, wrapBareMath } from "@/components/MathText";
import FigureEditor from "@/components/FigureEditor";
import CropAdjuster from "@/components/CropAdjuster";
import { toast } from "sonner";

function classLabel(c) {
  if (["LKG", "UKG", "Play Group"].includes(c)) return c;
  const n = parseInt(c, 10);
  return isNaN(n) ? c : `Class ${n}`;
}
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.15em] text-slate-400">{label}</div>
      <div className="text-xl font-bold text-slate-900 dark:text-slate-100 mt-0.5" style={{ fontFamily: "Outfit" }}>{value}</div>
    </div>
  );
}

const ROMANS = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X", "XI", "XII", "XIII", "XIV", "XV"];


export default function TeacherExams({ onExit }) {
  const [view, setView] = useState("list"); // "list" | "build" | "results"
  const [resultsExam, setResultsExam] = useState(null);

  if (view === "build") return <ExamBuilder onDone={() => setView("list")} onCancel={() => setView("list")} />;
  if (view === "results" && resultsExam) return <ExamResults examId={resultsExam} onBack={() => setView("list")} />;

  return <ExamList onCreate={() => setView("build")} onOpenResults={(id) => { setResultsExam(id); setView("results"); }} onExit={onExit} />;
}

/* ───────────────────────── List of created tests ───────────────────────── */
function ExamList({ onCreate, onOpenResults, onExit }) {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/teacher/exams"); setExams(data.exams || []); }
      catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Could not load tests"); }
      finally { setLoading(false); }
    })();
  }, []);

  const del = async (e) => {
    if (e.submitted > 0) { toast.error("Students have already submitted — this test can't be deleted."); return; }
    if (!window.confirm(`Delete "${e.title}"? This can't be undone.`)) return;
    try {
      await api.delete(`/teacher/exam/${e.id}`);
      setExams((xs) => xs.filter((x) => x.id !== e.id));
      toast.success("Test deleted.");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail) || "Could not delete test"); }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto" data-testid="teacher-exams-page">
        {onExit && (
          <Button variant="ghost" size="sm" className="rounded-full mb-3 -ml-2" onClick={onExit} data-testid="exams-exit-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Teacher Guidance
          </Button>
        )}
        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">Teacher tools</div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>
              <FileText className="inline h-7 w-7 text-indigo-500 mr-2 -mt-1" /> Tests &amp; Question Papers
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">Generate a question paper from lessons and send it to your students.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={onCreate} className="rounded-full bg-indigo-500 hover:bg-indigo-600 h-11" data-testid="exam-create-btn">
              <Plus className="h-4 w-4 mr-2" /> Create a test
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-slate-500">Loading…</div>
        ) : exams.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 p-12 text-center" data-testid="exams-empty">
            <FileText className="h-8 w-8 mx-auto text-slate-300 mb-3" />
            <div className="font-medium text-slate-900 dark:text-slate-100">No tests yet</div>
            <div className="text-slate-500 text-sm mt-1">Create your first question paper to get started.</div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="exams-list">
            {exams.map((e) => (
              <div key={e.id} className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5" data-testid={`exam-card-${e.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="font-semibold text-slate-900 dark:text-slate-100">{e.title}</div>
                  {e.locked
                    ? <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500">Closed</span>
                    : <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-900/40 text-emerald-600">Open</span>}
                </div>
                <div className="text-xs text-slate-500 mt-1">{e.subject} · {classLabel(e.class_name)} · Sec {e.section}</div>
                {e.categories?.length > 0 && (
                  <div className="flex items-center gap-1.5 mt-1.5" data-testid={`exam-cats-${e.id}`}>
                    {e.categories.map((c) => <span key={c} className={`text-[10px] px-2 py-0.5 rounded-full ${TIER_BADGE[c] || ""}`}>{TIER_LABEL[c] || c}</span>)}
                  </div>
                )}
                <div className="flex items-center gap-4 mt-3 text-sm text-slate-600 dark:text-slate-300">
                  <span className="inline-flex items-center gap-1"><ClipboardCheck className="h-4 w-4" /> {e.num_questions} Q · {e.total_marks} marks</span>
                  <span className="inline-flex items-center gap-1"><Users className="h-4 w-4" /> {e.submitted}/{e.assigned}</span>
                </div>
                <div className="text-xs text-slate-400 mt-2 inline-flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" /> Due {fmtDate(e.due_at)}</div>
                <div className="flex items-center gap-2 mt-4">
                  <Button onClick={() => onOpenResults(e.id)} variant="outline" size="sm" className="rounded-full flex-1" data-testid={`exam-results-${e.id}`}>
                    View submissions ({e.submitted})
                  </Button>
                  <Button
                    onClick={() => del(e)}
                    variant="outline" size="sm"
                    disabled={e.submitted > 0}
                    title={e.submitted > 0 ? "Students have already submitted — can't delete" : "Delete test"}
                    className="rounded-full text-rose-600 border-rose-200 hover:bg-rose-50 disabled:opacity-40"
                    data-testid={`exam-delete-${e.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

/* ───────────────────────── Builder ───────────────────────── */
const QTYPES = [
  { value: "mcq", label: "Multiple choice" },
  { value: "short", label: "Short answer" },
  { value: "long", label: "Long answer" },
];
const DEFAULT_SECTIONS = [
  { title: "Multiple choice questions", type: "mcq", count: 5, marks: 1 },
  { title: "Answer the following", type: "short", count: 5, marks: 2 },
  { title: "Answer the following in steps", type: "short", count: 4, marks: 3 },
  { title: "Solve the following", type: "long", count: 2, marks: 4 },
];
const ASSESSMENT_OPTIONS = [
  "Practice test",
  "Formative Assessment (FA-01)",
  "Formative Assessment (FA-02)",
  "I TERM",
  "Formative Assessment (FA-03)",
  "Formative Assessment (FA-04)",
  "II TERM",
];

// Kannada display names for the paper header (used when the paper language is Kannada).
const ASSESSMENT_KN = {
  "Practice test": "ಅಭ್ಯಾಸ ಪರೀಕ್ಷೆ",
  "Formative Assessment (FA-01)": "ಮೊದಲನೆಯ ಸಂಕಲನಾತ್ಮಕ ಪರೀಕ್ಷೆ",
  "Formative Assessment (FA-02)": "ಎರಡನೆಯ ಸಂಕಲನಾತ್ಮಕ ಪರೀಕ್ಷೆ",
  "Formative Assessment (FA-03)": "ಮೂರನೆಯ ಸಂಕಲನಾತ್ಮಕ ಪರೀಕ್ಷೆ",
  "Formative Assessment (FA-04)": "ನಾಲ್ಕನೆಯ ಸಂಕಲನಾತ್ಮಕ ಪರೀಕ್ಷೆ",
  "I TERM": "ಮೊದಲನೆಯ ಸೆಮಿಸ್ಟರ್ ಪರೀಕ್ಷೆ",
  "II TERM": "ಎರಡನೆಯ ಸೆಮಿಸ್ಟರ್ ಪರೀಕ್ಷೆ",
};
const SUBJECT_KN = {
  "Kannada": "ಕನ್ನಡ", "English": "ಇಂಗ್ಲಿಷ್", "Hindi": "ಹಿಂದಿ",
  "Mathematics": "ಗಣಿತ", "Maths": "ಗಣಿತ", "Science": "ವಿಜ್ಞಾನ",
  "Social Studies": "ಸಮಾಜ ವಿಜ್ಞಾನ", "Social": "ಸಮಾಜ ವಿಜ್ಞಾನ", "EVS": "ಪರಿಸರ ಅಧ್ಯಯನ",
};
const MONTH_KN = ["ಜನವರಿ", "ಫೆಬ್ರವರಿ", "ಮಾರ್ಚ್", "ಏಪ್ರಿಲ್", "ಮೇ", "ಜೂನ್", "ಜುಲೈ", "ಆಗಸ್ಟ್", "ಸೆಪ್ಟೆಂಬರ್", "ಅಕ್ಟೋಬರ್", "ನವೆಂಬರ್", "ಡಿಸೆಂಬರ್"];
const MONTH_EN = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

// Translate an English duration string ("1 hour 30 min") to Kannada ("1 ಗಂಟೆ 30 ನಿಮಿಷಗಳು").
function durationKn(s) {
  if (!s) return s;
  return String(s)
    .replace(/(\d+)\s*(hours?|hrs?|hr)/gi, "$1 ಗಂಟೆ")
    .replace(/(\d+)\s*(minutes?|mins?|min)/gi, "$1 ನಿಮಿಷಗಳು")
    .replace(/&/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}
// Class value without the English "Class" word (Kannada papers show only the number).
function classNumberOnly(c) {
  if (["LKG", "UKG", "Play Group"].includes(c)) return c;
  const n = parseInt(c, 10);
  return isNaN(n) ? c : String(n);
}


// Difficulty / performance tiers (mirrors backend CATEGORY_TIERS).
const CATEGORY_TIERS = [
  { key: "advanced", label: "Advanced", range: "Above 70%", cls: "emerald" },
  { key: "standard", label: "Standard", range: "40% – 70%", cls: "amber" },
  { key: "foundation", label: "Foundation", range: "Below 40%", cls: "rose" },
];
const TIER_LABEL = Object.fromEntries(CATEGORY_TIERS.map((t) => [t.key, t.label]));
const TIER_BADGE = {
  advanced: "bg-emerald-50 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300",
  standard: "bg-amber-50 dark:bg-amber-900/40 text-amber-600 dark:text-amber-300",
  foundation: "bg-rose-50 dark:bg-rose-900/40 text-rose-600 dark:text-rose-300",
};

// Localized paper labels keyed by the language code returned from the backend.
const PAPER_LABELS = {
  en: { cls: "Class", subject: "Subject", time: "Time", max: "Max Marks", name: "Name", roll: "Roll No" },
  kn: { cls: "ತರಗತಿ", subject: "ವಿಷಯ", time: "ಸಮಯ", max: "ಗರಿಷ್ಠ ಅಂಕಗಳು", name: "ಹೆಸರು", roll: "ಕ್ರಮ ಸಂಖ್ಯೆ" },
  hi: { cls: "कक्षा", subject: "विषय", time: "समय", max: "अधिकतम अंक", name: "नाम", roll: "अनुक्रमांक" },
  sa: { cls: "कक्षा", subject: "विषयः", time: "समयः", max: "पूर्णाङ्काः", name: "नाम", roll: "क्रमाङ्कः" },
  mr: { cls: "इयत्ता", subject: "विषय", time: "वेळ", max: "एकूण गुण", name: "नाव", roll: "हजेरी क्र." },
  te: { cls: "తరగతి", subject: "విషయం", time: "సమయం", max: "గరిష్ఠ మార్కులు", name: "పేరు", roll: "రోల్ నం." },
  ta: { cls: "வகுப்பு", subject: "பாடம்", time: "நேரம்", max: "அதிகபட்ச மதிப்பெண்கள்", name: "பெயர்", roll: "வரிசை எண்" },
  ml: { cls: "ക്ലാസ്", subject: "വിഷയം", time: "സമയം", max: "പരമാവധി മാർക്ക്", name: "പേര്", roll: "റോൾ നമ്പർ" },
  ur: { cls: "جماعت", subject: "مضمون", time: "وقت", max: "کل نمبر", name: "نام", roll: "رول نمبر" },
};
// Google-font family per language so Indic scripts render crisply in the PDF.
const PAPER_FONT = {
  kn: "Noto Sans Kannada", hi: "Noto Sans Devanagari", sa: "Noto Sans Devanagari",
  mr: "Noto Sans Devanagari", te: "Noto Sans Telugu", ta: "Noto Sans Tamil",
  ml: "Noto Sans Malayalam", ur: "Noto Nastaliq Urdu",
};

function ExamBuilder({ onDone, onCancel }) {
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [classFilter, setClassFilter] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [pickedLessons, setPickedLessons] = useState([]);
  const [assessmentName, setAssessmentName] = useState("Formative Assessment (FA-01)");
  const [categories, setCategories] = useState([]); // difficulty tiers
  const [sections, setSections] = useState(DEFAULT_SECTIONS.map((s) => ({ ...s, id: crypto.randomUUID() })));
  const [qtypes, setQtypes] = useState(QTYPES.map((t) => ({ id: t.value, label: t.label, base: t.value, instruction: "", builtin: true })));
  const [instructions, setInstructions] = useState("");
  const [difficulty, setDifficulty] = useState(""); // "" mixed | easy | medium | hard
  const [generating, setGenerating] = useState(false);
  const [paper, setPaper] = useState(null);
  const [genError, setGenError] = useState(null);

  // ── Fill-in-the-Blanks add-on ──
  const [includeBlanks, setIncludeBlanks] = useState(false);
  const [bankBlanks, setBankBlanks] = useState([]);      // from the manual bank
  const [pickedBank, setPickedBank] = useState([]);      // selected bank question ids
  const [aiBlankCount, setAiBlankCount] = useState(0);   // extra AI-generated blanks
  const [blankMarks, setBlankMarks] = useState(1);
  // ── Match the Following add-on (auto-graded, 1 mark/pair) ──
  const [includeMatch, setIncludeMatch] = useState(false);
  const [bankMatch, setBankMatch] = useState([]);
  const [pickedMatch, setPickedMatch] = useState([]);
  const [aiMatchCount, setAiMatchCount] = useState(0);
  // ── Complete the Poem add-on (print-only) ──
  const [includePoem, setIncludePoem] = useState(false);
  const [bankPoem, setBankPoem] = useState([]);
  const [pickedPoem, setPickedPoem] = useState([]);
  const [aiPoemCount, setAiPoemCount] = useState(0);
  const [poemMarks, setPoemMarks] = useState(2);
  const subjectName = subjects.find((s) => s.id === subjectId)?.name || "";

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/teacher/guidance/classes"); setClasses(data.classes || []); if (data.classes?.length) setClassFilter(data.classes[0]); } catch {}
    })();
  }, []);
  useEffect(() => {
    if (!classFilter) return;
    (async () => {
      try { const { data } = await api.get("/teacher/guidance/subjects", { params: { class_name: classFilter } }); setSubjects(data || []); } catch { setSubjects([]); }
      setSubjectId(""); setLessons([]); setPickedLessons([]);
    })();
  }, [classFilter]);
  useEffect(() => {
    if (!subjectId) { setLessons([]); return; }
    (async () => {
      try { const { data } = await api.get("/teacher/guidance/lessons", { params: { subject_id: subjectId } }); setLessons(data || []); } catch { setLessons([]); }
      setPickedLessons([]);
    })();
  }, [subjectId]);

  // Load question types (built-in + UtilityAdmin custom types) for the chosen subject.
  useEffect(() => {
    const subjName = subjects.find((s) => s.id === subjectId)?.name;
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/exam/question-types", { params: subjName ? { subject: subjName } : {} });
        if (!cancel && data?.types?.length) setQtypes(data.types);
      } catch { /* keep built-ins */ }
    })();
    return () => { cancel = true; };
  }, [subjectId, subjects]);

  const toggleLesson = (id) => setPickedLessons((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const allLessonsSelected = lessons.length > 0 && pickedLessons.length === lessons.length;

  // Load the manual Fill-in-the-Blanks bank for the chosen class + subject.
  const loadBank = () => {
    if (!classFilter || !subjectName) { setBankBlanks([]); return; }
    api.get("/blanks/questions", { params: { class_name: classFilter, subject: subjectName } })
      .then(({ data }) => setBankBlanks(data.questions || []))
      .catch(() => setBankBlanks([]));
  };
  useEffect(() => { setBankBlanks([]); setPickedBank([]); loadBank(); }, [classFilter, subjectName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Match & Poem banks (same class+subject scope).
  const loadMatch = () => {
    if (!classFilter || !subjectName) { setBankMatch([]); return; }
    api.get("/match/questions", { params: { class_name: classFilter, subject: subjectName } })
      .then(({ data }) => setBankMatch(data.questions || [])).catch(() => setBankMatch([]));
  };
  const loadPoem = () => {
    if (!classFilter || !subjectName) { setBankPoem([]); return; }
    api.get("/poem/questions", { params: { class_name: classFilter, subject: subjectName } })
      .then(({ data }) => setBankPoem(data.questions || [])).catch(() => setBankPoem([]));
  };
  useEffect(() => { setPickedMatch([]); setPickedPoem([]); loadMatch(); loadPoem(); }, [classFilter, subjectName]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleMatch = (id) => setPickedMatch((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const togglePoem = (id) => setPickedPoem((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const shuffleArr = (arr) => { const a = [...arr]; for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; };

  const [editBank, setEditBank] = useState(null); // bank question being edited
  const deleteBankQ = async (id) => {
    if (!window.confirm("Delete this bank question?")) return;
    try {
      await api.delete(`/blanks/questions/${id}`);
      setBankBlanks((b) => b.filter((q) => q.id !== id));
      setPickedBank((p) => p.filter((x) => x !== id));
      toast.success("Deleted.");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Could not delete."); }
  };

  const toggleBank = (id) => setPickedBank((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  // ── Paper templates (reusable layouts per class) ──
  const [templates, setTemplates] = useState([]);
  const loadTemplates = () => {
    if (!classFilter) { setTemplates([]); return; }
    api.get("/teacher/exam/templates", { params: { class_name: classFilter } })
      .then(({ data }) => setTemplates(data.templates || []))
      .catch(() => setTemplates([]));
  };
  useEffect(loadTemplates, [classFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveTemplate = async () => {
    const name = window.prompt("Name this template (e.g. FA Standard, Half-Yearly):", assessmentName || "");
    if (!name || !name.trim()) return;
    if (!sections.length) { toast.error("Add at least one section first."); return; }
    try {
      await api.post("/teacher/exam/templates", {
        name: name.trim(),
        class_name: classFilter,
        subject: subjectName,
        assessment_name: assessmentName,
        difficulty,
        categories,
        instructions,
        blank_marks: parseInt(blankMarks, 10) || 1,
        sections: sections.map((s) => ({ title: s.title, type: s.type, count: parseInt(s.count, 10) || 1, marks: parseInt(s.marks, 10) || 1 })),
      });
      toast.success("Template saved.");
      loadTemplates();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Could not save template."); }
  };

  const applyTemplate = (t) => {
    if (!t) return;
    if (t.assessment_name) setAssessmentName(t.assessment_name);
    setDifficulty(t.difficulty || "");
    setCategories(t.categories || []);
    setInstructions(t.instructions || "");
    if (t.blank_marks) setBlankMarks(t.blank_marks);
    setSections((t.sections || []).map((s) => ({
      id: crypto.randomUUID(),
      title: s.title || "",
      type: qtypes.some((q) => q.id === s.type) ? s.type : "short",
      count: s.count || 1,
      marks: s.marks || 1,
    })));
    toast.success(`Loaded "${t.name}".`);
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await api.delete(`/teacher/exam/templates/${id}`);
      setTemplates((ts) => ts.filter((t) => t.id !== id));
      toast.success("Template deleted.");
    } catch { toast.error("Could not delete template."); }
  };

  // ── Saved papers (full generated papers, private, auto-expire after 1 week) ──
  const [savedPapers, setSavedPapers] = useState([]);
  const [restoreState, setRestoreState] = useState(null);
  const loadSaved = () => {
    api.get("/teacher/exam/saved")
      .then(({ data }) => setSavedPapers(data.saved || []))
      .catch(() => setSavedPapers([]));
  };
  useEffect(loadSaved, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openSaved = async (id) => {
    try {
      const { data } = await api.get(`/teacher/exam/saved/${id}`);
      setRestoreState(data.editable || null);
      setPaper({ ...(data.paper || {}) });
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not open the saved paper.");
      loadSaved();
    }
  };

  const deleteSaved = async (id) => {
    if (!window.confirm("Delete this saved paper?")) return;
    try {
      await api.delete(`/teacher/exam/saved/${id}`);
      setSavedPapers((ps) => ps.filter((p) => p.id !== id));
      toast.success("Saved paper deleted.");
    } catch { toast.error("Could not delete saved paper."); }
  };

  const toggleCategory = (key) => setCategories((c) => c.includes(key) ? c.filter((x) => x !== key) : [...c, key]);

  const updateSection = (id, patch) => setSections((ss) => ss.map((s) => s.id === id ? { ...s, ...patch } : s));
  const removeSection = (id) => setSections((ss) => ss.filter((s) => s.id !== id));
  const addSection = () => setSections((ss) => [...ss, { id: crypto.randomUUID(), title: "Answer the following", type: "short", count: 3, marks: 2 }]);

  const totalQuestions = useMemo(() => sections.reduce((a, s) => a + (parseInt(s.count, 10) || 0), 0), [sections]);
  const totalMarks = useMemo(() => sections.reduce((a, s) => a + (parseInt(s.count, 10) || 0) * (parseInt(s.marks, 10) || 0), 0), [sections]);

  const generate = async () => {
    if (!pickedLessons.length) { toast.error("Select at least one lesson."); return; }
    if (!sections.length) { toast.error("Add at least one section."); return; }
    if (totalQuestions === 0) { toast.error("Each section needs at least one question."); return; }
    setGenerating(true);
    setGenError(null);
    try {
      const wantAiBlanks = includeBlanks && aiBlankCount > 0;
      const wantAiMatch = includeMatch && aiMatchCount > 0;
      const wantAiPoem = includePoem && aiPoemCount > 0;
      const [{ data }, blanksResp, matchResp, poemResp] = await Promise.all([
        api.post("/teacher/exam/generate", {
          lesson_ids: pickedLessons,
          assessment_name: assessmentName,
          categories,
          difficulty,
          sections: sections.map((s) => {
            const qt = qtypes.find((t) => t.id === s.type);
            const base = qt?.base || (["mcq", "short", "long"].includes(s.type) ? s.type : "short");
            return {
              title: s.title, type: base,
              count: parseInt(s.count, 10) || 1, marks: parseInt(s.marks, 10) || 1,
              type_label: qt && !qt.builtin ? qt.label : "",
              instruction: qt && !qt.builtin ? (qt.instruction || "") : "",
            };
          }),
          instructions,
        }),
        wantAiBlanks
          ? api.post("/teacher/exam/generate-blanks", { lesson_ids: pickedLessons, count: parseInt(aiBlankCount, 10) })
          : Promise.resolve(null),
        wantAiMatch
          ? api.post("/teacher/exam/generate-match", { lesson_ids: pickedLessons, count: parseInt(aiMatchCount, 10) })
          : Promise.resolve(null),
        wantAiPoem
          ? api.post("/teacher/exam/generate-poem", { lesson_ids: pickedLessons, count: parseInt(aiPoemCount, 10) })
          : Promise.resolve(null),
      ]);

      const nextIndex = () => (data.sections || []).reduce((m, s) => Math.max(m, s.index || 0), 0) + 1;
      const romanOf = (n) => ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X", "XI", "XII", "XIII", "XIV"][n - 1] || `${n}`;

      const lang = data.language || "en";
      const SEC_TITLES = {
        blanks: { en: "Fill in the blanks", kn: "ಬಿಟ್ಟ ಸ್ಥಳ ತುಂಬಿರಿ", hi: "रिक्त स्थान भरिए" },
        match: { en: "Match the following", kn: "ಹೊಂದಿಸಿ ಬರೆಯಿರಿ", hi: "मिलान कीजिए" },
        poem: { en: "Complete the poem", kn: "ಪದ್ಯ ಪೂರ್ಣಗೊಳಿಸಿ", hi: "कविता पूरी कीजिए" },
      };
      const secTitle = (k) => (SEC_TITLES[k][lang] || SEC_TITLES[k].en);

      // Merge Fill-in-the-Blanks (bank picks + AI-generated) as a dedicated section.
      if (includeBlanks) {
        const marks = parseInt(blankMarks, 10) || 1;
        const bankItems = bankBlanks.filter((q) => pickedBank.includes(q.id)).map((q) => ({ sentence: q.sentence, blanks: q.blanks }));
        const allBlanks = [...bankItems, ...(blanksResp?.data?.questions || [])];
        if (allBlanks.length) {
          const idx = nextIndex();
          data.sections = [...(data.sections || []), { index: idx, roman: romanOf(idx), title: secTitle("blanks"), type: "blanks" }];
          data.questions = [...(data.questions || []), ...allBlanks.map((b) => ({
            id: crypto.randomUUID(), type: "blanks", prompt: b.sentence, blanks: b.blanks, marks, section_index: idx, section_title: secTitle("blanks"),
          }))];
        }
      }

      // Merge Match the Following (auto-graded, 1 mark/pair).
      if (includeMatch) {
        const bankItems = bankMatch.filter((q) => pickedMatch.includes(q.id)).map((q) => ({ title: q.title, pairs: q.pairs }));
        const allMatch = [...bankItems, ...(matchResp?.data?.questions || [])];
        if (allMatch.length) {
          const idx = nextIndex();
          data.sections = [...(data.sections || []), { index: idx, roman: romanOf(idx), title: secTitle("match"), type: "match" }];
          data.questions = [...(data.questions || []), ...allMatch.map((m) => ({
            id: crypto.randomUUID(), type: "match",
            prompt: m.title || secTitle("match"),
            pairs: m.pairs,
            right_options: shuffleArr((m.pairs || []).map((p) => p.right)),
            marks: (m.pairs || []).length,
            section_index: idx, section_title: secTitle("match"),
          }))];
        }
      }

      // Merge Complete the Poem (print-only / manual online).
      if (includePoem) {
        const marks = parseInt(poemMarks, 10) || 1;
        const bankItems = bankPoem.filter((q) => pickedPoem.includes(q.id)).map((q) => ({ title: q.title, poem: q.poem, alt_poem: q.alt_poem }));
        const allPoem = [...bankItems, ...(poemResp?.data?.questions || [])];
        if (allPoem.length) {
          const idx = nextIndex();
          data.sections = [...(data.sections || []), { index: idx, roman: romanOf(idx), title: secTitle("poem"), type: "poem" }];
          data.questions = [...(data.questions || []), ...allPoem.map((p) => ({
            id: crypto.randomUUID(), type: "poem",
            prompt: p.title || secTitle("poem"), poem: p.poem, alt_poem: p.alt_poem || "",
            marks, section_index: idx, section_title: secTitle("poem"),
          }))];
        }
      }

      setPaper({ ...data, class_name: data.class_name || classFilter, assessment_name: assessmentName, categories, difficulty });
    } catch (e) {
      setGenError(e);
    } finally { setGenerating(false); }
  };

  // Build a header-only paper — no sections, no questions — so the teacher
  // gets just the school header + Class/Subject/Time/Max Marks + Name/Roll No
  // block on a clean A4 sheet and can hand-write / hand-draw the entire paper
  // themselves. No AI call, no lesson selection needed.
  const generateTemplate = () => {
    if (!subjectId) { toast.error("Pick a subject so the header shows it correctly."); return; }
    // Match the AI flow: pick the paper language from the subject name so
    // language subjects (Kannada / Hindi / English / Sanskrit / …) render
    // the header in that script.
    const s = (subjectName || "").toLowerCase();
    const langCode =
      s.includes("kannada") || s.includes("ಕನ್ನಡ") ? "kn" :
      s.includes("hindi")   || s.includes("हिन्दी") || s.includes("हिंदी") ? "hi" :
      s.includes("sanskrit") ? "sa" :
      s.includes("marathi") ? "mr" :
      s.includes("telugu") ? "te" :
      s.includes("tamil") ? "ta" :
      s.includes("malayalam") ? "ml" :
      s.includes("urdu") ? "ur" :
      "en";
    setPaper({
      subject: subjectName,
      class_name: classFilter,
      assessment_name: assessmentName,
      categories,
      difficulty,
      language: langCode,
      lessons: [],
      sections: [],
      questions: [],
      is_template: true,
    });
    toast.success("Blank template created — just the header. Write the rest by hand or add sections later.");
  };

  const romans = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];

  return (
    <Layout>
      <div className="max-w-3xl mx-auto" data-testid="exam-builder">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" className="rounded-full" onClick={onCancel} data-testid="builder-back"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>Create a question paper</h1>
        </div>

        <div className="rounded-3xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 space-y-6">
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">Assessment name</Label>
            <Select value={assessmentName} onValueChange={setAssessmentName}>
              <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="builder-assessment-name"><SelectValue placeholder="Select assessment" /></SelectTrigger>
              <SelectContent>{ASSESSMENT_OPTIONS.map((o) => <SelectItem key={o} value={o} data-testid={`assessment-opt-${o}`}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">Class</Label>
              <Select value={classFilter} onValueChange={setClassFilter}>
                <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="builder-class"><SelectValue placeholder="Class" /></SelectTrigger>
                <SelectContent>{classes.map((c) => <SelectItem key={c} value={c}>{classLabel(c)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">Subject</Label>
              <Select value={subjectId} onValueChange={setSubjectId} disabled={!subjects.length}>
                <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="builder-subject"><SelectValue placeholder={subjects.length ? "Subject" : "No subjects"} /></SelectTrigger>
                <SelectContent>{subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {subjectId && (
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">Lessons to include</Label>
                {lessons.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setPickedLessons(allLessonsSelected ? [] : lessons.map((l) => l.id))}
                    className="text-xs font-medium text-indigo-600 dark:text-indigo-300 hover:underline"
                    data-testid="builder-select-all-lessons"
                  >
                    {allLessonsSelected ? "Clear all" : "Select all"}
                  </button>
                )}
              </div>
              {lessons.length === 0 ? (
                <div className="text-sm text-amber-600 mt-2">No lessons with uploaded material in this subject.</div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-2 mt-2" data-testid="builder-lessons">
                  {lessons.map((l) => (
                    <button key={l.id} type="button" onClick={() => toggleLesson(l.id)}
                      className={`text-left text-sm px-3 py-2 rounded-xl border transition-colors ${pickedLessons.includes(l.id) ? "border-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-200" : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300"}`}
                      data-testid={`builder-lesson-${l.id}`}>
                      {pickedLessons.includes(l.id) && <CheckCircle2 className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />}{l.title}
                    </button>
                  ))}
                </div>
              )}
              {lessons.length > 0 && (
                <div className="text-xs text-slate-400 mt-1.5" data-testid="builder-lessons-count">{pickedLessons.length} of {lessons.length} selected</div>
              )}
            </div>
          )}

          {/* Reusable paper templates for this class */}
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-3" data-testid="builder-templates">
            <div className="flex items-center justify-between gap-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-400 flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> Templates {classFilter && `· ${classLabel(classFilter)}`}</Label>
              <Button type="button" size="sm" variant="outline" className="rounded-full h-8" onClick={saveTemplate} data-testid="builder-save-template">
                <Plus className="h-3.5 w-3.5 mr-1" /> Save current as template
              </Button>
            </div>
            {templates.length === 0 ? (
              <p className="text-xs text-slate-400 mt-2">No saved templates for this class yet. Set up your sections & marks, then save them to reuse next time.</p>
            ) : (
              <div className="flex flex-wrap gap-2 mt-2" data-testid="builder-template-list">
                {templates.map((t) => (
                  <div key={t.id} className="flex items-center gap-1 rounded-full border border-slate-200 dark:border-slate-700 pl-3 pr-1 py-1 bg-slate-50 dark:bg-slate-900/40" data-testid={`template-chip-${t.id}`}>
                    <button type="button" onClick={() => applyTemplate(t)} className="text-xs font-medium text-slate-700 dark:text-slate-200 hover:text-indigo-600" data-testid={`template-load-${t.id}`}>
                      {t.name} <span className="text-slate-400">({(t.sections || []).length} sec)</span>
                    </button>
                    <button type="button" onClick={() => deleteTemplate(t.id)} className="text-slate-400 hover:text-rose-500 p-0.5" data-testid={`template-del-${t.id}`} aria-label="Delete template"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Saved papers — full generated papers you can re-open, download or send (kept for 1 week) */}
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-3" data-testid="builder-saved-papers">
            <Label className="text-xs uppercase tracking-[0.15em] text-slate-400 flex items-center gap-1.5"><Save className="h-3.5 w-3.5" /> Saved papers <span className="text-slate-300 normal-case tracking-normal">· kept 1 week</span></Label>
            {savedPapers.length === 0 ? (
              <p className="text-xs text-slate-400 mt-2">No saved papers yet. Generate a paper, then tap <b>Save paper</b> in the preview to keep it here and send it whenever you want.</p>
            ) : (
              <div className="space-y-1.5 mt-2" data-testid="saved-papers-list">
                {savedPapers.map((p) => (
                  <div key={p.id} className="flex items-center gap-2 rounded-xl border border-slate-200 dark:border-slate-700 px-3 py-2 bg-slate-50 dark:bg-slate-900/40" data-testid={`saved-paper-${p.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{p.title || `${p.subject} paper`}</div>
                      <div className="text-[11px] text-slate-400 truncate">{[p.subject, p.class_name && classLabel(p.class_name), p.assessment_name, `${p.question_count} Q`].filter(Boolean).join(" · ")}</div>
                    </div>
                    <Button type="button" size="sm" variant="outline" className="rounded-full h-8" onClick={() => openSaved(p.id)} data-testid={`saved-open-${p.id}`}>
                      <ArrowRight className="h-3.5 w-3.5 mr-1" /> Open
                    </Button>
                    <button type="button" onClick={() => deleteSaved(p.id)} className="text-slate-400 hover:text-rose-500 p-1 shrink-0" data-testid={`saved-del-${p.id}`} aria-label="Delete saved paper"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <CategoryPicker
            categories={categories}
            toggleCategory={toggleCategory}
          />

          <DifficultyPicker difficulty={difficulty} setDifficulty={setDifficulty} />

          {/* Fill-in-the-Blanks add-on — bank picks + optional AI generation */}
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4" data-testid="builder-blanks-panel">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={includeBlanks} onChange={(e) => setIncludeBlanks(e.target.checked)} data-testid="builder-blanks-toggle" />
              <span className="text-sm font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-1.5"><PenTool className="h-4 w-4 text-emerald-500" /> Include Fill-in-the-Blanks</span>
            </label>
            {includeBlanks && (
              <div className="mt-3 space-y-3">
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.12em] text-slate-400">From your question bank {bankBlanks.length > 0 && `(${bankBlanks.length})`}</Label>
                  {bankBlanks.length === 0 ? (
                    <p className="text-xs text-slate-400 mt-1">No bank questions for this class + subject yet. Build them in UtilityAdmin → Utility, or generate with AI below.</p>
                  ) : (
                    <div className="mt-1.5 space-y-1.5 max-h-52 overflow-auto pr-1" data-testid="builder-blanks-bank">
                      {bankBlanks.map((q) => (
                        <div key={q.id} className={`flex items-center gap-1 rounded-xl border transition-colors ${pickedBank.includes(q.id) ? "border-emerald-400 bg-emerald-50 dark:bg-emerald-900/30" : "border-slate-200 dark:border-slate-700"}`} data-testid={`builder-blank-row-${q.id}`}>
                          <button type="button" onClick={() => toggleBank(q.id)}
                            className={`flex-1 text-left text-sm px-3 py-2 ${pickedBank.includes(q.id) ? "text-emerald-700 dark:text-emerald-200" : "text-slate-600 dark:text-slate-300"}`}
                            data-testid={`builder-blank-bank-${q.id}`}>
                            {pickedBank.includes(q.id) && <CheckCircle2 className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />}
                            {(q.sentence || "").replace(/_{2,}/g, "＿＿＿")}
                          </button>
                          <button type="button" onClick={() => setEditBank(q)} className="text-slate-400 hover:text-indigo-500 p-1 shrink-0" data-testid={`builder-blank-edit-${q.id}`} aria-label="Edit"><PenTool className="h-3.5 w-3.5" /></button>
                          <button type="button" onClick={() => deleteBankQ(q.id)} className="text-slate-400 hover:text-rose-500 p-1 pr-1.5 shrink-0" data-testid={`builder-blank-del-${q.id}`} aria-label="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.12em] text-slate-400">Generate with AI (extra)</Label>
                    <Input type="number" min={0} max={20} value={aiBlankCount} onChange={(e) => setAiBlankCount(e.target.value)} className="h-9 rounded-lg mt-1 text-center" data-testid="builder-blanks-ai-count" />
                    <p className="text-[10px] text-slate-400 mt-1">From the selected lessons.</p>
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.12em] text-slate-400">Marks each</Label>
                    <Input type="number" min={1} value={blankMarks} onChange={(e) => setBlankMarks(e.target.value)} className="h-9 rounded-lg mt-1 text-center" data-testid="builder-blanks-marks" />
                    <p className="text-[10px] text-slate-400 mt-1">All-or-nothing grading.</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Match the Following add-on */}
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4" data-testid="builder-match-panel">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={includeMatch} onChange={(e) => setIncludeMatch(e.target.checked)} data-testid="builder-match-toggle" />
              <span className="text-sm font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-1.5"><LinkIcon className="h-4 w-4 text-sky-500" /> Include Match the Following</span>
            </label>
            {includeMatch && (
              <div className="mt-3 space-y-3">
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.12em] text-slate-400">From your question bank {bankMatch.length > 0 && `(${bankMatch.length})`}</Label>
                  {bankMatch.length === 0 ? (
                    <p className="text-xs text-slate-400 mt-1">None yet. Build them in UtilityAdmin → Utility, or generate with AI below.</p>
                  ) : (
                    <div className="mt-1.5 space-y-1.5 max-h-52 overflow-auto pr-1" data-testid="builder-match-bank">
                      {bankMatch.map((q) => (
                        <button key={q.id} type="button" onClick={() => toggleMatch(q.id)}
                          className={`w-full text-left text-sm px-3 py-2 rounded-xl border transition-colors ${pickedMatch.includes(q.id) ? "border-sky-400 bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-200" : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300"}`}
                          data-testid={`builder-match-bank-${q.id}`}>
                          {pickedMatch.includes(q.id) && <CheckCircle2 className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />}
                          {q.title} <span className="text-slate-400">({(q.pairs || []).length} pairs)</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.12em] text-slate-400">Generate with AI (extra)</Label>
                  <Input type="number" min={0} max={15} value={aiMatchCount} onChange={(e) => setAiMatchCount(e.target.value)} className="h-9 rounded-lg mt-1 text-center w-32" data-testid="builder-match-ai-count" />
                  <p className="text-[10px] text-slate-400 mt-1">Auto-graded online · 1 mark per correct pair. Printed as two columns.</p>
                </div>
              </div>
            )}
          </div>

          {/* Complete the Poem add-on (print-only) */}
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4" data-testid="builder-poem-panel">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={includePoem} onChange={(e) => setIncludePoem(e.target.checked)} data-testid="builder-poem-toggle" />
              <span className="text-sm font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-1.5"><FileText className="h-4 w-4 text-violet-500" /> Include Complete the Poem</span>
            </label>
            {includePoem && (
              <div className="mt-3 space-y-3">
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.12em] text-slate-400">From your question bank {bankPoem.length > 0 && `(${bankPoem.length})`}</Label>
                  {bankPoem.length === 0 ? (
                    <p className="text-xs text-slate-400 mt-1">None yet. Build them in UtilityAdmin → Utility, or generate with AI below.</p>
                  ) : (
                    <div className="mt-1.5 space-y-1.5 max-h-52 overflow-auto pr-1" data-testid="builder-poem-bank">
                      {bankPoem.map((q) => (
                        <button key={q.id} type="button" onClick={() => togglePoem(q.id)}
                          className={`w-full text-left text-sm px-3 py-2 rounded-xl border transition-colors ${pickedPoem.includes(q.id) ? "border-violet-400 bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-200" : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300"}`}
                          data-testid={`builder-poem-bank-${q.id}`}>
                          {pickedPoem.includes(q.id) && <CheckCircle2 className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />}
                          {q.title}{q.alt_poem ? " · +OR" : ""}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.12em] text-slate-400">Generate with AI (extra)</Label>
                    <Input type="number" min={0} max={15} value={aiPoemCount} onChange={(e) => setAiPoemCount(e.target.value)} className="h-9 rounded-lg mt-1 text-center" data-testid="builder-poem-ai-count" />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.12em] text-slate-400">Marks each</Label>
                    <Input type="number" min={1} value={poemMarks} onChange={(e) => setPoemMarks(e.target.value)} className="h-9 rounded-lg mt-1 text-center" data-testid="builder-poem-marks" />
                  </div>
                </div>
                <p className="text-[10px] text-slate-400">Print-only (written by hand). Prints the poem with blanks{"; "}any OR-alternative appears below.</p>
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">Sections</Label>
              <span className="text-xs text-slate-500" data-testid="builder-total">{totalQuestions} Q · {totalMarks} marks</span>
            </div>
            <div className="space-y-2 mt-2" data-testid="builder-sections">
              {sections.map((s, i) => {
                const secTotal = (parseInt(s.count, 10) || 0) * (parseInt(s.marks, 10) || 0);
                return (
                  <div key={s.id} className="rounded-2xl border border-slate-200 dark:border-slate-700 p-3" data-testid={`builder-section-${i}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-indigo-500 w-6 shrink-0">{romans[i]}.</span>
                      <Input value={s.title} onChange={(e) => updateSection(s.id, { title: e.target.value })} placeholder="Section title" className="h-9 rounded-lg flex-1" data-testid={`section-title-${i}`} />
                      <button onClick={() => removeSection(s.id)} className="text-slate-400 hover:text-rose-500 shrink-0" data-testid={`section-del-${i}`} aria-label="Remove section"><Trash2 className="h-4 w-4" /></button>
                    </div>
                    <div className="grid grid-cols-4 gap-2 mt-2 items-end">
                      <div className="col-span-2">
                        <Label className="text-[10px] text-slate-400">Type</Label>
                        <Select value={s.type} onValueChange={(v) => {
                          // Selecting a question type also sets the section
                          // HEADING to that type's label (e.g. the Kannada
                          // instruction the UtilityAdmin wrote), unless the
                          // teacher has typed their own custom heading.
                          const newLabel = qtypes.find((t) => t.id === v)?.label || "";
                          const oldLabel = qtypes.find((t) => t.id === s.type)?.label || "";
                          const keepCustom = s.title && s.title !== oldLabel;
                          updateSection(s.id, { type: v, ...(keepCustom ? {} : { title: newLabel }) });
                        }}>
                          <SelectTrigger className="h-9 rounded-lg mt-0.5" data-testid={`section-type-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{qtypes.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[10px] text-slate-400"># Questions</Label>
                        <Input type="number" min={1} value={s.count} onChange={(e) => updateSection(s.id, { count: e.target.value })} className="h-9 rounded-lg mt-0.5 text-center" data-testid={`section-count-${i}`} />
                      </div>
                      <div>
                        <Label className="text-[10px] text-slate-400">Marks each</Label>
                        <Input type="number" min={1} value={s.marks} onChange={(e) => updateSection(s.id, { marks: e.target.value })} className="h-9 rounded-lg mt-0.5 text-center" data-testid={`section-marks-${i}`} />
                      </div>
                    </div>
                    <div className="text-right text-xs text-slate-500 mt-1.5">{s.count || 0} × {s.marks || 0} = <span className="font-semibold text-slate-700 dark:text-slate-200">{secTotal}</span> marks</div>
                  </div>
                );
              })}
            </div>
            <Button variant="outline" size="sm" onClick={addSection} className="rounded-full mt-2" data-testid="section-add">
              <Plus className="h-4 w-4 mr-1.5" /> Add section
            </Button>
          </div>

          {/* Special instructions — kept at the bottom */}
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">Special instructions (optional)</Label>
            <Textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="e.g. focus on definitions, keep language simple…" className="rounded-xl mt-1" data-testid="builder-instructions" />
          </div>

          <Button onClick={generate} disabled={generating} className="rounded-full h-11 bg-indigo-500 hover:bg-indigo-600 w-full" data-testid="builder-generate">
            {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            {generating ? "Generating paper…" : "Generate question paper"}
          </Button>

          <Button
            onClick={generateTemplate}
            disabled={generating}
            variant="outline"
            className="rounded-full h-11 w-full border-indigo-200 text-indigo-600 hover:bg-indigo-50 dark:border-indigo-900/40 dark:hover:bg-indigo-950/30"
            data-testid="builder-generate-template"
          >
            <FileText className="h-4 w-4 mr-2" /> Generate question paper template
          </Button>
          <p className="text-[11px] text-slate-500 -mt-2 text-center">
            Header only — school name, assessment, Class · Subject · Time · Max Marks and Name / Roll No lines. You write everything else by hand.
          </p>

          {genError && <AiServiceNotice error={genError} onRetry={generate} retrying={generating} />}
        </div>
      </div>

      {paper && (
        <PaperPreview
          paper={paper}
          classFilter={classFilter}
          subjects={subjects}
          subjectId={subjectId}
          initialSection=""
          categories={categories}
          initialStudentIds={[]}
          restore={restoreState}
          onSaved={loadSaved}
          onClose={() => { setPaper(null); setRestoreState(null); }}
          onSent={() => { setPaper(null); setRestoreState(null); onDone(); }}
        />
      )}

      <BankQuestionEditor
        question={editBank}
        onClose={() => setEditBank(null)}
        onSaved={() => { setEditBank(null); loadBank(); }}
      />
    </Layout>
  );
}

/* ───────────── Edit a Fill-in-the-Blanks bank question in place ───────────── */
function BankQuestionEditor({ question, onClose, onSaved }) {
  const [sentence, setSentence] = useState("");
  const [blanks, setBlanks] = useState([]);
  const [saving, setSaving] = useState(false);
  const letters = ["A", "B", "C", "D"];
  const nBlanks = (sentence.match(/_{2,}/g) || []).length;

  useEffect(() => {
    if (!question) return;
    setSentence(question.sentence || "");
    setBlanks((question.blanks || []).map((b) => ({ options: [...(b.options || ["", "", "", ""])].slice(0, 4), correct_index: b.correct_index ?? 0 })));
  }, [question]);

  // Keep option-sets in sync with the number of blanks typed.
  useEffect(() => {
    setBlanks((prev) => {
      const n = Math.max(1, nBlanks);
      if (prev.length === n) return prev;
      if (prev.length < n) return [...prev, ...Array.from({ length: n - prev.length }, () => ({ options: ["", "", "", ""], correct_index: 0 }))];
      return prev.slice(0, n);
    });
  }, [nBlanks]);

  const setOpt = (bi, oi, v) => setBlanks((p) => p.map((b, i) => i === bi ? { ...b, options: b.options.map((o, j) => j === oi ? v : o) } : b));
  const setCorrect = (bi, oi) => setBlanks((p) => p.map((b, i) => i === bi ? { ...b, correct_index: oi } : b));

  const save = async () => {
    if (nBlanks === 0) return toast.error("Type ___ where the answer goes.");
    if (nBlanks !== blanks.length) return toast.error("Blanks and option-sets don't match.");
    for (let i = 0; i < blanks.length; i++) if (blanks[i].options.some((o) => !o.trim())) return toast.error(`Blank ${i + 1}: fill all four options.`);
    setSaving(true);
    try {
      await api.put(`/blanks/questions/${question.id}`, { subject: question.subject, class_name: question.class_name, sentence, blanks });
      toast.success("Question updated.");
      onSaved();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Could not save."); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!question} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="bank-edit-dialog">
        <DialogHeader><DialogTitle>Edit fill-in-the-blanks question</DialogTitle></DialogHeader>
        <div className="space-y-4 max-h-[70vh] overflow-auto pr-1">
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">Sentence</Label>
            <Textarea value={sentence} onChange={(e) => setSentence(e.target.value)} className="rounded-xl mt-1 min-h-[70px]" data-testid="bank-edit-sentence" />
            <p className="text-[11px] text-slate-400 mt-1">Use <b>___</b> for each blank. <span className="text-emerald-600">{nBlanks} detected.</span></p>
          </div>
          {blanks.map((b, bi) => (
            <div key={bi} className="rounded-xl border border-slate-100 dark:border-slate-700/60 p-3" data-testid={`bank-edit-blank-${bi}`}>
              <div className="text-xs font-semibold text-slate-500 mb-2">Blank {bi + 1} — pick the correct option</div>
              <div className="space-y-2">
                {b.options.map((o, oi) => (
                  <div key={oi} className="flex items-center gap-2">
                    <button type="button" onClick={() => setCorrect(bi, oi)}
                      className={`h-6 w-6 shrink-0 rounded-full border flex items-center justify-center text-[11px] font-bold ${b.correct_index === oi ? "bg-emerald-500 border-emerald-500 text-white" : "border-slate-300 text-slate-400"}`}
                      data-testid={`bank-edit-correct-${bi}-${oi}`}>{letters[oi]}</button>
                    <Input value={o} onChange={(e) => setOpt(bi, oi, e.target.value)} className="h-9 rounded-lg" data-testid={`bank-edit-opt-${bi}-${oi}`} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full" data-testid="bank-edit-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-full bg-indigo-500 hover:bg-indigo-600" data-testid="bank-edit-save">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ───────────────────────── Question difficulty picker ───────────────────────── */
const DIFFICULTY_OPTIONS = [
  { key: "", label: "Mixed", desc: "A balanced mix (~⅓ each) of Easy, Medium and Hard", source: "≈⅓ verbatim exercise + ⅓ reworded exercise + ⅓ new non-exercise questions." },
  { key: "easy", label: "Easy", desc: "Questions taken word-for-word from the lesson's LBA assessment PDF (falls back to the lesson's own exercise questions if no LBA is uploaded)", source: "STRICTLY the questions in the lesson's uploaded LBA (Lesson-Based Assessment) PDF, reproduced verbatim — no rewording. If a lesson has no LBA, its printed exercise questions are used instead." },
  { key: "medium", label: "Medium", desc: "The same exercise questions, reworded / asked differently", source: "Based on the exercise questions but rephrased — same points, never copied verbatim." },
  { key: "hard", label: "Hard", desc: "New higher-order questions, NOT from the exercises", source: "Strictly NOT from the exercises (application/reasoning), still answerable within the lesson." },
];

function DifficultyPicker({ difficulty, setDifficulty }) {
  const active = DIFFICULTY_OPTIONS.find((o) => o.key === difficulty) || DIFFICULTY_OPTIONS[0];
  return (
    <div data-testid="builder-difficulty">
      <div className="flex items-center gap-2">
        <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">Question difficulty</Label>
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" data-testid="difficulty-legend-btn" aria-label="What does each level pull from?" className="text-slate-400 hover:text-indigo-500 transition-colors">
              <Info className="h-3.5 w-3.5" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-80" data-testid="difficulty-legend">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Where each level pulls from</p>
            <ul className="space-y-2">
              {DIFFICULTY_OPTIONS.map((o) => (
                <li key={o.key || "mixed"} className="text-sm">
                  <span className="font-semibold text-indigo-600 dark:text-indigo-300">{o.label}</span>
                  <span className="block text-xs text-slate-500 dark:text-slate-400">{o.source}</span>
                </li>
              ))}
            </ul>
          </PopoverContent>
        </Popover>
      </div>
      <p className="text-xs text-slate-400 mt-0.5">Every level stays strictly within the selected lesson(s) — nothing out of syllabus.</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
        {DIFFICULTY_OPTIONS.map((o) => {
          const on = o.key === difficulty;
          return (
            <button
              key={o.key || "mixed"}
              type="button"
              onClick={() => setDifficulty(o.key)}
              data-testid={`difficulty-${o.key || "mixed"}`}
              className={`text-sm font-medium px-3 py-2 rounded-xl border transition-colors ${on ? "border-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-200" : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-indigo-300"}`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400 mt-2" data-testid="difficulty-desc">{active.desc}.</p>
    </div>
  );
}

/* ───────────────────────── Category (difficulty) picker ───────────────────────── */
function CategoryPicker({ categories, toggleCategory }) {
  return (
    <div data-testid="builder-category">
      <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">Target students by performance (optional)</Label>
      <p className="text-xs text-slate-400 mt-0.5">Optionally target students in the selected performance tier(s). You'll pick the section — and the matching students will be preselected — when you send the test. Leave empty to send to everyone.</p>
      <div className="grid sm:grid-cols-3 gap-2 mt-2">
        {CATEGORY_TIERS.map((t) => {
          const active = categories.includes(t.key);
          return (
            <button
              key={t.key} type="button" onClick={() => toggleCategory(t.key)}
              className={`text-left px-3 py-2.5 rounded-xl border transition-colors ${active ? "border-indigo-400 bg-indigo-50 dark:bg-indigo-900/30" : "border-slate-200 dark:border-slate-700 hover:border-indigo-300"}`}
              data-testid={`builder-cat-${t.key}`}
            >
              <div className="flex items-center gap-1.5">
                {active && <CheckCircle2 className="h-3.5 w-3.5 text-indigo-500" />}
                <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{t.label}</span>
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">{t.range}</div>
            </button>
          );
        })}
      </div>
      {categories.length > 0 && (
        <div className="text-xs text-slate-500 mt-2" data-testid="builder-recipient-count">
          Targeting {categories.map((c) => TIER_LABEL[c]).join(", ")}. Students in {categories.length > 1 ? "these tiers" : "this tier"} of the chosen section will be preselected at send time.
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── Preview + edit + send ───────────────────────── */
function PaperPreview({ paper, classFilter, subjects, subjectId, initialSection = "", categories = [], initialStudentIds = [], restore = null, onSaved, onClose, onSent }) {
  const { user } = useAuth();
  const hasCategories = categories.length > 0;
  const [questions, setQuestions] = useState(restore?.questions || paper.questions);
  const [sections, setSections] = useState(initialSection ? [initialSection] : ["A", "B", "C", "D"]);
  const [selectedSections, setSelectedSections] = useState(initialSection ? [initialSection] : []);
  const [title, setTitle] = useState(restore?.title || `${paper.subject} Test`);
  const [assessmentName, setAssessmentName] = useState(restore?.assessmentName || paper.assessment_name || "Formative Assessment (FA-01)");
  const [campus, setCampus] = useState(restore?.campus || "");
  const [timeAllowed, setTimeAllowed] = useState(restore?.timeAllowed || "1 hour 30 min");
  const [examMonth, setExamMonth] = useState(() => {
    if (restore?.examMonth) return restore.examMonth;
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [saving, setSaving] = useState(false);
  const [due, setDue] = useState("");
  const [specific, setSpecific] = useState(hasCategories);
  const [roster, setRoster] = useState([]);
  const [pickedStudents, setPickedStudents] = useState(initialStudentIds);
  const [sending, setSending] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [printing, setPrinting] = useState(false);

  const subjectName = subjects.find((s) => s.id === subjectId)?.name || paper.subject;
  const lang = paper.language || "en";
  const L = PAPER_LABELS[lang] || PAPER_LABELS.en;
  // Month/year appended to the assessment heading (localized), driven by the exam-month picker.
  const [mYear, mMonth] = examMonth.split("-");
  const mIdx = Math.max(0, Math.min(11, (parseInt(mMonth, 10) || 1) - 1));
  const monthYearLabel = `${(lang === "kn" ? MONTH_KN : MONTH_EN)[mIdx]} - ${mYear}`;
  const assessmentBase = lang === "kn" ? (ASSESSMENT_KN[assessmentName] || assessmentName) : assessmentName;
  const assessmentDisplay = `${assessmentBase}, ${monthYearLabel}`;
  const subjectDisplay = lang === "kn" ? (SUBJECT_KN[subjectName] || subjectName) : subjectName;
  // Formal assessments (FA-01/02/03/04, I TERM, II TERM) must never be dispatched to student apps.
  // Only "Practice test" (or a custom name) can be sent to students.
  const isFormalExam = /\bFA-?\d+\b|\bTERM\b/i.test(assessmentName || "");
  const schoolDisplay = lang === "kn" ? "ತ್ರಿಶೂಲ ಹೈ ಸ್ಕೂಲ್, ದಾವಣಗೆರೆ" : "TRISHUL HIGH SCHOOL";
  // Class shown without the English "Class" word on Kannada papers (number only).
  const classDisplay = lang === "kn" ? classNumberOnly(classFilter) : classLabel(classFilter);
  const timeDisplay = lang === "kn" ? durationKn(timeAllowed) : timeAllowed;
  const paperFont = PAPER_FONT[lang] || "";
  const rtl = lang === "ur";
  const totalMarks = useMemo(() => questions.reduce((a, q) => a + (parseInt(q.marks, 10) || 0), 0), [questions]);

  // Group questions by their section (falls back to a single "Questions" section).
  // `secOrder` lets the teacher reorder the sections (romans renumber by position).
  const [secOrder, setSecOrder] = useState(restore?.secOrder || null);
  const paperSections = useMemo(() => {
    const base = paper.sections?.length
      ? paper.sections.map((s) => ({ ...s }))
      : [{ index: 1, roman: "I", title: "Questions", type: "mixed" }];
    const byIndex = new Map(base.map((s) => [s.index, s]));
    const order = (secOrder && secOrder.length ? secOrder : base.map((s) => s.index)).filter((i) => byIndex.has(i));
    base.forEach((s) => { if (!order.includes(s.index)) order.push(s.index); });
    let pos = 0;
    const out = [];
    order.forEach((idx) => {
      const s = byIndex.get(idx);
      const qs = questions.filter((q) => (q.section_index || 1) === s.index);
      if (!qs.length) return;
      const marks = qs.reduce((a, q) => a + (parseInt(q.marks, 10) || 0), 0);
      out.push({ ...s, roman: ROMANS[pos] || `${pos + 1}`, questions: qs, marksTotal: marks });
      pos++;
    });
    return out;
  }, [questions, paper.sections, secOrder]);

  const moveSection = (secIndex, dir) => {
    const cur = paperSections.map((s) => s.index);
    const i = cur.indexOf(secIndex);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= cur.length) return;
    [cur[i], cur[j]] = [cur[j], cur[i]];
    setSecOrder(cur);
  };

  // Questions flattened in the current (possibly reordered) section order, with
  // section_index/title renumbered to match — used when sending & rendering.
  const orderedQuestions = () =>
    paperSections.flatMap((s, pos) =>
      s.questions.map((q) => ({ ...q, section_index: pos + 1, section_title: s.title })));


  useEffect(() => {
    (async () => {
      try {
        // Sections this teacher may target for THIS subject: class-teacher
        // sections + assigned sections + timetable subject-teacher sections.
        const { data } = await api.get("/teacher/exam/eligible-sections", {
          params: { class_name: classFilter, subject: subjectName || "" },
        });
        const secs = data.sections || [];
        setSections(secs);
        setSelectedSections((prev) => {
          const kept = prev.filter((s) => secs.includes(s));
          return kept.length ? kept : (secs.length ? [secs[0]] : []);
        });
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classFilter, subjectName]);

  useEffect(() => {
    if (!specific) { setRoster([]); setPickedStudents([]); return; }
    if (!selectedSections.length) { setRoster([]); setPickedStudents([]); return; }
    (async () => {
      try {
        // Build the recipient roster across ALL selected sections (tagging each
        // student with its section). For tier targeting, preselect the matching
        // students; otherwise keep any still-valid manual selections.
        const all = [];
        for (const sec of selectedSections) {
          if (hasCategories) {
            const { data } = await api.get("/teacher/exam/categories", { params: { class_name: classFilter, section: sec } });
            (data.students || []).filter((s) => categories.includes(s.tier)).forEach((s) => all.push({ ...s, _section: sec }));
          } else {
            const { data } = await api.get("/students", { params: { class_name: classFilter, section: sec } });
            (data || []).forEach((s) => all.push({ ...s, _section: sec }));
          }
        }
        setRoster(all);
        if (hasCategories) setPickedStudents(all.map((s) => s.id));
        else setPickedStudents((p) => p.filter((id) => all.some((s) => s.id === id)));
      } catch { setRoster([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specific, classFilter, selectedSections.join(",")]);

  const updateQ = (id, patch) => setQuestions((qs) => qs.map((q) => q.id === id ? { ...q, ...patch } : q));
  const removeQ = (id) => setQuestions((qs) => qs.filter((q) => q.id !== id));

  const [regenId, setRegenId] = useState(null); // `${id}` (question) or `${id}:fig`
  const lessonIds = (paper.lessons || []).map((l) => l.id);
  const mathish = /math|phys|geometry|mensuration|algebra/i.test(subjectName || "");

  const regenerateQuestion = async (q, figureOnly) => {
    const key = figureOnly ? `${q.id}:fig` : q.id;
    setRegenId(key);
    try {
      const { data } = await api.post("/teacher/exam/regenerate-question", {
        lesson_ids: lessonIds,
        section_title: q.section_title || "",
        qtype: q.type,
        marks: parseInt(q.marks, 10) || 1,
        avoid: figureOnly ? "" : q.prompt,
        figure_only: figureOnly,
        existing_prompt: q.prompt,
      });
      if (figureOnly) {
        if (data.figure_svg) {
          updateQ(q.id, { figure_svg: data.figure_svg, figure_shapes: undefined });
          toast.success("Figure generated");
        } else {
          toast("No figure was generated — your existing figure was kept.");
        }
      } else {
        updateQ(q.id, {
          prompt: data.prompt ?? q.prompt,
          options: q.type === "mcq" ? (data.options || []) : q.options,
          answer_index: q.type === "mcq" ? (data.answer_index ?? -1) : q.answer_index,
          answer_text: data.answer_text ?? "",
          figure_svg: data.figure_svg || "",
        });
        toast.success("Question regenerated");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Regeneration failed. Please try again.");
    } finally {
      setRegenId(null);
    }
  };

  const [uploadingImgId, setUploadingImgId] = useState(null);
  const imgInputRef = useRef(null);
  const imgTargetRef = useRef(null);
  const [figEditQ, setFigEditQ] = useState(null); // question being drawn/edited

  // ---- Reuse a saved sketch (from Teacher Guidance → My Notes) inside a question ----
  const [sketchPickerQ, setSketchPickerQ] = useState(null); // question id the picker targets
  const [savedSketches, setSavedSketches] = useState(null); // null = not loaded yet
  const extractSvg = (content) => {
    const m = String(content || "").match(/<svg[\s\S]*?<\/svg>/i);
    return m ? m[0] : "";
  };
  const openSketchPicker = async (q) => {
    setSketchPickerQ(q.id);
    try {
      const { data } = await api.get("/teacher/guidance/notes");
      const sketches = (data.notes || [])
        .map((n) => ({ ...n, svg: extractSvg(n.content) }))
        .filter((n) => n.svg);
      setSavedSketches(sketches);
    } catch {
      if (savedSketches === null) setSavedSketches([]);
      toast.error("Could not load your saved sketches");
    }
  };

  const pickImage = (q) => { imgTargetRef.current = q.id; imgInputRef.current?.click(); };

  const onImageFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const qid = imgTargetRef.current;
    if (!file || !qid) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image too large (max 5 MB)."); return; }
    setUploadingImgId(qid);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/teacher/exam/question-image", fd);
      updateQ(qid, { figure_image: data.url || "" });
      toast.success("Image attached");
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Could not attach image");
    } finally {
      setUploadingImgId(null);
    }
  };

  const buildPaperInner = (logoSrc) => {
    const esc = (t) => String(t ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const letters = lang === "kn" ? ["ಅ", "ಆ", "ಇ", "ಈ", "ಉ", "ಊ"] : ["a", "b", "c", "d", "e", "f"];
    const secHtml = paperSections.map((s) => {
      const qHtml = s.questions.map((q, i) => {
        if (q.type === "blanks") {
          const sentence = mathToHtmlPdf((q.prompt || "").replace(/_{2,}/g, "＿＿＿＿"), esc);
          const bl = (q.blanks || []).map((b, bi) => {
            const opts = (b.options || []).map((o, oi) => `<span class="opt">${letters[oi]}) ${mathToHtmlPdf(o, esc)}</span>`).join("");
            const label = (q.blanks || []).length > 1 ? `Blank ${bi + 1}: ` : "";
            return `<div class="opts">${label}${opts}</div>`;
          }).join("");
          return `<div class="q"><span class="qn">${i + 1}.</span><span class="qt">${sentence}</span><span class="qm">[${q.marks}]</span></div>${bl}`;
        }
        if (q.type === "match") {
          const lefts = (q.pairs || []).map((p) => p.left);
          const rights = (q.right_options && q.right_options.length) ? q.right_options : (q.pairs || []).map((p) => p.right);
          const rows = lefts.map((lft, ri) => {
            const rl = letters[ri] || `${ri + 1}`;
            const rgt = rights[ri] != null ? mathToHtmlPdf(rights[ri], esc) : "";
            return `<tr><td class="mnum">${ri + 1}.</td><td class="mcell">${mathToHtmlPdf(lft, esc)}</td><td class="mans">_____</td><td class="mnum">${rl})</td><td class="mcell">${rgt}</td></tr>`;
          }).join("");
          return `<div class="q"><span class="qn">${i + 1}.</span><span class="qt">${mathToHtmlPdf(q.prompt || "Match the following", esc)}</span><span class="qm">[${q.marks}]</span></div><table class="mtable">${rows}</table>`;
        }
        if (q.type === "poem") {
          const poemHtml = mathToHtmlPdf(q.poem || "", esc).replace(/\n/g, "<br/>").replace(/_{2,}/g, "＿＿＿＿＿");
          const altHtml = (q.alt_poem || "").trim()
            ? `<div class="poemor">ಅಥವಾ / OR</div><div class="poem">${mathToHtmlPdf(q.alt_poem, esc).replace(/\n/g, "<br/>").replace(/_{2,}/g, "＿＿＿＿＿")}</div>`
            : "";
          return `<div class="q"><span class="qn">${i + 1}.</span><span class="qt">${mathToHtmlPdf(q.prompt || "Complete the poem", esc)}</span><span class="qm">[${q.marks}]</span></div><div class="poem">${poemHtml}</div>${altHtml}`;
        }
        const opts = q.type === "mcq" && q.options?.length
          ? `<div class="opts">${q.options.map((o, oi) => `<span class="opt">${letters[oi]}) ${mathToHtmlPdf(o, esc)}</span>`).join("")}</div>`
          : "";
        const fig = q.figure_svg ? `<div class="qfig">${q.figure_svg}</div>` : "";
        const img = q.figure_image ? `<div class="qimg"><img src="${API_BASE}${q.figure_image}" crossorigin="anonymous"/></div>` : "";
        return `<div class="q"><span class="qn">${i + 1}.</span><span class="qt">${mathToHtmlPdf(q.prompt, esc)}</span><span class="qm">[${q.marks}]</span></div>${fig}${img}${opts}`;
      }).join("");
      const cnt = s.questions.length;
      const per = s.questions[0]?.marks || s.marks || 1;
      return `<div class="sec"><div class="sechead"><span><b>${s.roman}.</b> ${esc(s.title)}</span><span class="weight">${cnt} × ${per} = ${s.marksTotal}</span></div>${qHtml}</div>`;
    }).join("");

    // Scoped styles (prefixed with .qp-root so they never leak into the app)
    const fontStack = paperFont
      ? `'${paperFont}','Noto Sans Kannada','Noto Sans Devanagari',Georgia,'Times New Roman',serif`
      : `Georgia,'Times New Roman',serif`;
    const style = `
      .qp-root{font-family:${fontStack};color:#111;width:794px;padding:36px 44px;background:#fff;box-sizing:border-box}
      .qp-root *{box-sizing:border-box}
      .qp-root .head{position:relative;text-align:center;min-height:70px;padding-bottom:2px;display:flex;align-items:center;justify-content:center}
      .qp-root .head img{position:absolute;left:0;top:50%;transform:translateY(-50%);height:66px;width:66px;object-fit:contain;border-radius:50%}
      .qp-root .htext{max-width:80%}
      .qp-root .school{font-size:23px;font-weight:700;letter-spacing:1px}
      .qp-root .campus{font-size:13px;color:#333}
      .qp-root .assess{font-size:14px;font-weight:700;margin-top:3px;text-transform:uppercase}
      .qp-root .metatop{display:flex;justify-content:space-between;font-size:13px;margin:6px 2px 0;border-bottom:2px solid #111;padding-bottom:8px}
      .qp-root .meta{display:flex;justify-content:space-between;font-size:13px;margin:10px 2px;padding-bottom:6px}
      .qp-root .sec{margin-top:16px}
      .qp-root .sechead{display:flex;justify-content:space-between;font-size:14px;font-weight:700;border-bottom:1px solid #ccc;padding-bottom:3px;margin-bottom:8px}
      .qp-root .weight{font-weight:700}
      .qp-root .q{display:flex;gap:6px;font-size:13.5px;margin-top:8px;align-items:flex-start}
      .qp-root .qn{font-weight:700;min-width:20px}.qp-root .qt{flex:1}.qp-root .qm{white-space:nowrap;color:#333}
      .qp-root .opts{display:flex;flex-wrap:wrap;gap:8px 24px;margin:4px 0 0 26px;font-size:13px}
      .qp-root sup{font-size:.72em;vertical-align:super;line-height:0}
      .qp-root sub{font-size:.72em;vertical-align:sub;line-height:0}
      .qp-root .qfig{margin:8px 0 4px 26px}
      .qp-root .qfig svg{max-width:280px;height:auto;max-height:200px}
      .qp-root .qimg{margin:8px 0 4px 26px}
      .qp-root .qimg img{max-width:320px;height:auto;max-height:240px;display:block}
      .qp-root .mtable{margin:6px 0 4px 26px;border-collapse:collapse;font-size:13px;width:calc(100% - 26px)}
      .qp-root .mtable td{padding:3px 8px;vertical-align:top}
      .qp-root .mtable .mnum{font-weight:700;white-space:nowrap}
      .qp-root .mtable .mcell{padding-right:20px}
      .qp-root .mtable .mans{color:#555;white-space:nowrap;padding:0 14px}
      .qp-root .poem{margin:6px 0 4px 26px;font-size:13.5px;line-height:1.7;white-space:pre-wrap}
      .qp-root .poemor{margin:8px 0 4px 26px;font-weight:700;text-align:center}
    `;
    return `<style>${style}</style>
      <div class="head">
        <img class="qp-logo" src="${logoSrc}" alt="logo"/>
        <div class="htext">
          <div class="school">${esc(schoolDisplay)}</div>
          ${campus ? `<div class="campus">${esc(campus)}</div>` : ""}
          <div class="assess">${esc(assessmentDisplay)}</div>
          ${hasCategories ? `<div class="campus" style="margin-top:2px">Category: ${esc(categories.map((c) => TIER_LABEL[c]).join(", "))}</div>` : ""}
        </div>
      </div>
      <div class="metatop"><span><b>${esc(L.cls)}:</b> ${esc(classDisplay)} &nbsp; <b>${esc(L.subject)}:</b> ${esc(subjectDisplay)}</span><span><b>${esc(L.time)}:</b> ${esc(timeDisplay)} &nbsp; <b>${esc(L.max)}:</b> ${totalMarks}</span></div>
      <div class="meta"><span>${esc(L.name)}: _____________________________</span><span>${esc(L.roll)}: __________</span></div>
      ${secHtml}`;
  };

  const openPreview = async () => {
    const origin = window.location.origin;
    let logoSrc = `${origin}/trishul-logo.png`;
    setPreviewHtml(buildPaperInner(logoSrc));
  };

  // Editable Word download — a TABLE-based .doc that mirrors the PDF layout
  // (Word ignores flexbox, so we lay everything out with tables + inline styles).
  const downloadWord = async () => {
    const origin = window.location.origin;
    let logo = "";
    try {
      const resp = await fetch(`${origin}/trishul-logo.png`);
      const blob = await resp.blob();
      logo = await new Promise((res) => { const r = new FileReader(); r.onloadend = () => res(r.result); r.readAsDataURL(blob); });
    } catch { /* logo optional */ }

    const wLetters = lang === "kn" ? ["ಅ", "ಆ", "ಇ", "ಈ", "ಉ", "ಊ"] : ["a", "b", "c", "d", "e", "f"];
    const wSections = paperSections.map((s) => {
      const per = s.questions[0]?.marks || s.marks || 1;
      return {
        roman: s.roman || "",
        title: s.title || "",
        meta: `${s.questions.length} × ${per} = ${s.marksTotal}`,
        questions: s.questions.map((q) => ({
          type: q.type || "short",
          prompt: q.prompt || "",
          marks: q.marks || 1,
          options: q.options || [],
          blanks: (q.blanks || []).map((b) => ({ options: b.options || [] })),
          pairs: q.pairs || [],
          right_options: q.right_options || [],
          poem: q.poem || "",
        })),
      };
    });
    const catLine = hasCategories ? `Category: ${categories.map((c) => TIER_LABEL[c]).join(", ")}` : "";
    const fname = `${assessmentName || "Question_Paper"}_${subjectName || ""}`;

    try {
      const res = await api.post("/teacher/exam/docx", {
        filename: fname,
        language: lang,
        school: schoolDisplay,
        campus: campus || "",
        assessment: assessmentDisplay,
        category_line: catLine,
        class_label: L.cls, class_value: String(classDisplay),
        subject_label: L.subject, subject_value: subjectDisplay,
        time_label: L.time, time_value: timeDisplay,
        max_label: L.max, max_value: String(totalMarks),
        name_label: L.name, roll_label: L.roll,
        letters: wLetters,
        logo,
        sections: wSections,
      }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${fname.replace(/[^\w-]+/g, "_")}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
      toast.success("Word document downloaded — opens on phone, tablet & desktop.");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not generate the Word file");
    }
  };

  const printPaper = async () => {
    const origin = window.location.origin;
    const container = document.createElement("div");
    container.className = "qp-root";
    if (rtl) container.setAttribute("dir", "rtl");

    // Load the Indic web-font (if any) so html2canvas captures crisp glyphs.
    if (paperFont) {
      try {
        const famParam = paperFont.replace(/ /g, "+");
        if (!document.querySelector(`link[data-paper-font="${paperFont}"]`)) {
          const link = document.createElement("link");
          link.rel = "stylesheet";
          link.setAttribute("data-paper-font", paperFont);
          link.href = `https://fonts.googleapis.com/css2?family=${famParam}:wght@400;700&display=swap`;
          document.head.appendChild(link);
        }
        await Promise.race([document.fonts.load(`700 16px '${paperFont}'`), new Promise((r) => setTimeout(r, 4000))]);
        await document.fonts.ready;
      } catch { /* font optional */ }
    }

    // Preload the logo as a data URL so html2canvas always captures it (no timing/CORS issues)
    let logoSrc = `${origin}/trishul-logo.png`;
    try {
      const resp = await fetch(`${origin}/trishul-logo.png`);
      const blob = await resp.blob();
      logoSrc = await new Promise((res) => { const r = new FileReader(); r.onloadend = () => res(r.result); r.readAsDataURL(blob); });
    } catch { /* logo optional */ }

    container.innerHTML = buildPaperInner(logoSrc);
    container.style.position = "absolute";
    container.style.left = "-9999px";
    container.style.top = "0";
    container.style.background = "#fff";
    document.body.appendChild(container);

    const filename = `${(assessmentName || "Question Paper").replace(/[^\w-]+/g, "_")}_${(subjectName || "").replace(/[^\w-]+/g, "_")}.pdf`;
    try {
      const html2canvas = (await import("html2canvas")).default;
      const { jsPDF } = await import("jspdf");

      // Ensure KaTeX (and any Indic) fonts are fully loaded before rasterising.
      try { await document.fonts.ready; } catch { /* noop */ }

      const SCALE = 3;  // high-res so Kannada / Indic glyphs stay razor-sharp
      const canvas = await html2canvas(container, { scale: SCALE, useCORS: true, backgroundColor: "#ffffff" });

      const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const footer = 8;                        // reserved space at the bottom for the page number
      const usableW = pageW - margin * 2;
      const usableH = pageH - margin - footer;

      // Height (in canvas px) that fits on ONE printable page — never overflow it.
      const pageContentPx = Math.floor((usableH / usableW) * canvas.width);

      // Allowed page-break positions (canvas px) = the TOP of every atomic block.
      // We only break BEFORE a block, so a question together with its options /
      // figure is never split across two pages. The FIRST question of a section
      // is not a break point, so its section heading always stays with it.
      const cRect = container.getBoundingClientRect();
      const topPx = (el) => Math.round((el.getBoundingClientRect().top - cRect.top) * SCALE);
      const cuts = new Set();
      container.querySelectorAll(".metatop, .meta, .sechead").forEach((el) => cuts.add(topPx(el)));
      container.querySelectorAll(".sec").forEach((sec) => {
        sec.querySelectorAll(":scope > .q").forEach((q, i) => { if (i > 0) cuts.add(topPx(q)); });
      });
      const cutList = Array.from(cuts).filter((y) => y > 0).sort((a, b) => a - b);

      const totalH = canvas.height;
      let start = 0;
      let pageNum = 0;
      while (start < totalH - 1) {
        let end = Math.min(start + pageContentPx, totalH);
        if (end < totalH) {
          // Snap this page's bottom to the last allowed break that still fits.
          let snap = -1;
          for (const y of cutList) { if (y > start + 20 && y <= end) snap = y; }
          if (snap > 0) end = snap;   // else a single block is taller than a page → hard cut
        }
        const sliceH = Math.max(1, Math.round(end - start));
        const slice = document.createElement("canvas");
        slice.width = canvas.width;
        slice.height = sliceH;
        const ctx = slice.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, slice.width, slice.height);
        ctx.drawImage(canvas, 0, start, canvas.width, sliceH, 0, 0, canvas.width, sliceH);
        const imgData = slice.toDataURL("image/jpeg", 0.95);   // high-res (scale 3) → crisp glyphs, compact size
        const imgH = (sliceH * usableW) / canvas.width;
        if (pageNum > 0) pdf.addPage();
        pdf.addImage(imgData, "JPEG", margin, margin, usableW, imgH);
        pageNum += 1;
        start = end;
      }

      const total = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= total; i++) {
        pdf.setPage(i);
        pdf.setFontSize(9);
        pdf.setTextColor(120);
        pdf.text(`${i} / ${total}`, pageW / 2, pageH - 4, { align: "center" });
      }
      pdf.save(filename);
    } catch (e) {
      toast.error("Could not generate the PDF. Please try again.");
    } finally {
      document.body.removeChild(container);
    }
  };

  const savePaper = async () => {
    if (!questions.length) { toast.error("The paper is empty."); return; }
    setSaving(true);
    try {
      await api.post("/teacher/exam/saved", {
        title: title || `${subjectName} paper`,
        subject: subjectName,
        class_name: classFilter,
        assessment_name: assessmentName,
        language: lang,
        paper: {
          ...paper,
          subject: subjectName,
          language: lang,
          questions,
          class_name: classFilter,
          assessment_name: assessmentName,
        },
        editable: { title, assessmentName, campus, timeAllowed, examMonth, secOrder, questions },
      });
      toast.success("Paper saved — find it under \"Saved papers\" to send anytime this week.");
      onSaved && onSaved();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not save the paper.");
    } finally { setSaving(false); }
  };

  const send = async () => {
    if (isFormalExam) { toast.error("Formal exams (FA / TERM) cannot be sent to students. Download the PDF/Word paper instead."); return; }
    if (!questions.length) { toast.error("The paper is empty."); return; }
    if (!due) { toast.error("Pick a due date."); return; }
    if (new Date(due) <= new Date()) { toast.error("Due date must be in the future."); return; }
    if (!selectedSections.length) { toast.error("Pick at least one section to send to."); return; }
    if (specific && pickedStudents.length === 0) { toast.error("Select at least one student to send to."); return; }
    setSending(true);
    try {
      const res = await api.post("/teacher/exam", {
        title, subject: subjectName, assessment_name: assessmentName,
        categories,
        lesson_titles: (paper.lessons || []).map((l) => l.title),
        class_name: classFilter, section: selectedSections[0], sections: selectedSections,
        student_ids: specific ? pickedStudents : [],
        questions: orderedQuestions().map((q) => ({ ...q, marks: parseInt(q.marks, 10) || 1 })),
        instructions: "", due_at: new Date(due).toISOString(),
      });
      const n = res.data?.count || selectedSections.length;
      toast.success(n > 1 ? `Test sent to ${n} sections.` : "Test sent to students.");
      onSent();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not send the test");
    } finally { setSending(false); }
  };

  const letters = lang === "kn" ? ["ಅ", "ಆ", "ಇ", "ಈ", "ಉ", "ಊ"] : ["a", "b", "c", "d", "e", "f"];

  return (
    <>
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto"
        data-testid="paper-preview"
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="relative flex items-center justify-center text-center min-h-[64px]">
            <img src="/trishul-logo.png" alt="Trishul High School" className="absolute left-0 top-1/2 -translate-y-1/2 h-14 w-14 rounded-full" data-testid="paper-logo" />
            <div className="max-w-[80%]">
              <div className="text-xl font-bold tracking-wide text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>{schoolDisplay}</div>
              {campus && <div className="text-xs text-slate-500">{campus}</div>}
              <div className="text-sm font-semibold uppercase text-slate-700 dark:text-slate-200 mt-0.5" data-testid="paper-assessment">{assessmentDisplay}</div>
              {hasCategories && (
                <div className="flex items-center justify-center gap-1.5 mt-1" data-testid="paper-categories">
                  {categories.map((c) => <span key={c} className={`text-[10px] px-2 py-0.5 rounded-full ${TIER_BADGE[c]}`}>{TIER_LABEL[c]}</span>)}
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-between text-xs text-slate-600 dark:text-slate-300 border-b-2 border-slate-800 dark:border-slate-500 pb-2 mt-1.5">
            <span><b>{L.cls}:</b> {classDisplay} · <b>{L.subject}:</b> {subjectDisplay}</span>
            <span><b>{L.time}:</b> {timeDisplay} · <b>{L.max}:</b> <span data-testid="paper-total-marks">{totalMarks}</span></span>
          </div>
          <DialogTitle className="sr-only">Question paper preview</DialogTitle>
          <DialogDescription className="sr-only">Review and edit the generated paper, choose recipients and due date.</DialogDescription>
        </DialogHeader>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Test title (for students app)</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="h-10 rounded-xl mt-1" data-testid="paper-title" />
          </div>
          <div>
            <Label className="text-xs">Assessment name</Label>
            <Select value={assessmentName} onValueChange={setAssessmentName}>
              <SelectTrigger className="h-10 rounded-xl mt-1" data-testid="paper-assessment-input"><SelectValue placeholder="Select assessment" /></SelectTrigger>
              <SelectContent>{ASSESSMENT_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Campus / address (optional)</Label>
            <Input value={campus} onChange={(e) => setCampus(e.target.value)} placeholder="e.g. Davangere Campus" className="h-10 rounded-xl mt-1" data-testid="paper-campus" />
          </div>
          <div>
            <Label className="text-xs">Time allowed</Label>
            <Input value={timeAllowed} onChange={(e) => setTimeAllowed(e.target.value)} className="h-10 rounded-xl mt-1" data-testid="paper-time" />
          </div>
          <div>
            <Label className="text-xs">Exam month (shown on heading)</Label>
            <Input type="month" value={examMonth} onChange={(e) => setExamMonth(e.target.value)} className="h-10 rounded-xl mt-1" data-testid="paper-month" />
          </div>
        </div>

        <div className="space-y-5 mt-3" data-testid="paper-questions">
          <div className="flex items-center justify-between">
            <div className="text-xs text-slate-500">{questions.length} question{questions.length === 1 ? "" : "s"} · {totalMarks} marks total · same template will be kept when you clear.</div>
            <Button
              variant="outline"
              size="sm"
              className="rounded-full text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700 dark:border-rose-900/40 dark:hover:bg-rose-950/40"
              disabled={!questions.length}
              data-testid="paper-clear-all"
              onClick={() => {
                if (!questions.length) return;
                if (!window.confirm(`Clear all ${questions.length} question${questions.length === 1 ? "" : "s"}? The sections, question types and marks (the paper template) will be kept — you can then type your own prompts into each blank slot.`)) return;
                setQuestions((qs) => qs.map((q) => ({
                  ...q,
                  prompt: "",
                  options: (q.options || []).map(() => ""),
                  blanks: (q.blanks || []).map((b) => ({ ...b, options: (b.options || []).map(() => "") })),
                  pairs: (q.pairs || []).map(() => ({ left: "", right: "" })),
                  right_options: (q.right_options || []).map(() => ""),
                  poem: q.poem !== undefined ? "" : q.poem,
                  alt_poem: q.alt_poem !== undefined ? "" : q.alt_poem,
                  figure_svg: "",
                  figure_shapes: undefined,
                  figure_image: "",
                })));
                toast.success("All questions cleared — template kept. Type your own prompts in each slot.");
              }}
            >
              <Trash2 className="h-4 w-4 mr-1.5" /> Clear all questions
            </Button>
          </div>
          <input ref={imgInputRef} type="file" accept="image/*" className="hidden" onChange={onImageFile} data-testid="q-image-input" />
          {paperSections.map((s, si) => (
            <div key={s.index} data-testid={`paper-section-${s.index}`}>
              <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 pb-1.5 mb-2">
                <div className="flex items-center gap-1.5">
                  <div className="flex flex-col -my-1 mr-0.5">
                    <button onClick={() => moveSection(s.index, -1)} disabled={si === 0} title="Move section up" className="text-slate-300 hover:text-indigo-500 disabled:opacity-30 disabled:hover:text-slate-300 leading-none" data-testid={`section-up-${s.index}`}><ChevronUp className="h-4 w-4" /></button>
                    <button onClick={() => moveSection(s.index, 1)} disabled={si === paperSections.length - 1} title="Move section down" className="text-slate-300 hover:text-indigo-500 disabled:opacity-30 disabled:hover:text-slate-300 leading-none" data-testid={`section-down-${s.index}`}><ChevronDown className="h-4 w-4" /></button>
                  </div>
                  <div className="font-semibold text-slate-800 dark:text-slate-100"><span className="text-indigo-500 mr-1">{s.roman}.</span>{s.title}</div>
                </div>
                <div className="text-xs font-semibold text-slate-500">{s.questions.length} × {s.questions[0]?.marks || s.marks || 1} = {s.marksTotal}</div>
              </div>
              <div className="space-y-3">
                {s.questions.map((q, i) => (
                  <div key={q.id} className="rounded-xl border border-slate-200 dark:border-slate-700 p-3">
                    <div className="flex items-start gap-2">
                      <span className="text-sm font-semibold text-slate-400 mt-2">{i + 1}.</span>
                      <Textarea value={q.prompt} onChange={(e) => updateQ(q.id, { prompt: e.target.value })} className="rounded-lg flex-1 min-h-[40px]" data-testid={`q-prompt-${q.id}`} />
                      <div className="flex flex-col items-end gap-1">
                        <Input type="number" min={1} value={q.marks} onChange={(e) => updateQ(q.id, { marks: e.target.value })} className="h-8 w-14 rounded-lg text-center text-xs" title="marks" data-testid={`q-marks-${q.id}`} />
                        <div className="flex items-center gap-2">
                          {!["blanks", "match", "poem"].includes(q.type) && <>
                          <button onClick={() => regenerateQuestion(q, false)} disabled={!!regenId} title="Regenerate this question" className="text-slate-400 hover:text-indigo-500 disabled:opacity-40" data-testid={`q-regen-${q.id}`}>
                            {regenId === q.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                          </button>
                          <button onClick={() => regenerateQuestion(q, true)} disabled={!!regenId} title={q.figure_svg ? "Regenerate figure with AI" : "Generate a figure with AI"} className="text-slate-400 hover:text-emerald-500 disabled:opacity-40" data-testid={`q-regen-fig-${q.id}`}>
                            {regenId === `${q.id}:fig` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shapes className="h-4 w-4" />}
                          </button>
                          <button onClick={() => setFigEditQ(q.id)} title="Draw / edit a figure yourself" className="text-slate-400 hover:text-violet-500" data-testid={`q-draw-fig-${q.id}`}>
                            <PenTool className="h-4 w-4" />
                          </button>
                          <button onClick={() => openSketchPicker(q)} title="Insert a saved sketch from My Notes" className="text-slate-400 hover:text-amber-500" data-testid={`q-saved-sketch-${q.id}`}>
                            <Shapes className="h-4 w-4 rotate-12" />
                          </button>
                          <button onClick={() => pickImage(q)} disabled={!!uploadingImgId} title="Attach an image to this question" className="text-slate-400 hover:text-sky-500 disabled:opacity-40" data-testid={`q-attach-${q.id}`}>
                            {uploadingImgId === q.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                          </button>
                          </>}
                          <button onClick={() => removeQ(q.id)} className="text-slate-400 hover:text-rose-500" data-testid={`q-del-${q.id}`}><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </div>
                    </div>
                    {hasMath(q.prompt) && (
                      <div className="mt-1.5 pl-6 text-sm text-slate-700 dark:text-slate-200" data-testid={`q-preview-${q.id}`}>
                        <MathInline text={q.prompt} />
                      </div>
                    )}
                    {q.figure_svg && (
                      <div
                        className="mt-2 ml-6 inline-block rounded-lg border border-slate-200 dark:border-slate-700 bg-white p-2 [&_svg]:max-w-[280px] [&_svg]:h-auto"
                        data-testid={`q-figure-${q.id}`}
                        dangerouslySetInnerHTML={{ __html: q.figure_svg }}
                      />
                    )}
                    {q.figure_image && (
                      <div className="mt-2 ml-6 relative inline-block" data-testid={`q-image-${q.id}`}>
                        <img src={`${API_BASE}${q.figure_image}`} alt="attached" className="max-w-[280px] max-h-[200px] rounded-lg border border-slate-200 dark:border-slate-700 bg-white" />
                        <button onClick={() => updateQ(q.id, { figure_image: "" })} title="Remove image" className="absolute -top-2 -right-2 bg-rose-500 text-white rounded-full p-0.5" data-testid={`q-image-remove-${q.id}`}><X className="h-3 w-3" /></button>
                      </div>
                    )}
                    {q.type === "blanks" && (
                      <div className="mt-2 pl-6 space-y-2" data-testid={`q-blanks-${q.id}`}>
                        {(q.blanks || []).map((b, bi) => (
                          <div key={bi} className="text-xs">
                            {(q.blanks || []).length > 1 && <div className="text-slate-400 mb-0.5">Blank {bi + 1}</div>}
                            <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                              {(b.options || []).map((o, oi) => (
                                <span key={oi} className={oi === b.correct_index ? "text-emerald-600 font-semibold" : "text-slate-500"}>{letters[oi]}) {o}{oi === b.correct_index ? " ✓" : ""}</span>
                              ))}
                            </div>
                          </div>
                        ))}
                        <div className="text-[11px] text-slate-400">Auto-graded · full marks only if every blank is correct.</div>
                      </div>
                    )}
                    {q.type === "match" && (
                      <div className="mt-2 pl-6" data-testid={`q-match-${q.id}`}>
                        <table className="text-xs">
                          <tbody>
                            {(q.pairs || []).map((p, pi) => (
                              <tr key={pi}>
                                <td className="pr-2 text-slate-400">{pi + 1}.</td>
                                <td className="pr-4 text-slate-700 dark:text-slate-200">{p.left}</td>
                                <td className="text-slate-400">→</td>
                                <td className="pl-3 text-emerald-600 font-medium">{p.right}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div className="text-[11px] text-slate-400 mt-1">Auto-graded · 1 mark per correct pair (right column is shuffled on the paper).</div>
                      </div>
                    )}
                    {q.type === "poem" && (
                      <div className="mt-2 pl-6" data-testid={`q-poem-${q.id}`}>
                        <div className="text-xs text-slate-700 dark:text-slate-200 whitespace-pre-wrap leading-relaxed">{(q.poem || "").replace(/_{2,}/g, "＿＿＿＿")}</div>
                        {(q.alt_poem || "").trim() && (
                          <>
                            <div className="text-[11px] font-semibold text-center text-slate-500 my-1">ಅಥವಾ / OR</div>
                            <div className="text-xs text-slate-700 dark:text-slate-200 whitespace-pre-wrap leading-relaxed">{(q.alt_poem || "").replace(/_{2,}/g, "＿＿＿＿")}</div>
                          </>
                        )}
                        <div className="text-[11px] text-slate-400 mt-1">Print-only · students write by hand.</div>
                      </div>
                    )}
                    {q.type === "mcq" && (
                      <div className="mt-2 pl-6 space-y-1.5">
                        {q.options.map((opt, oi) => (
                          <div key={oi} className="flex items-center gap-2">
                            <input type="radio" name={`ans-${q.id}`} checked={q.answer_index === oi} onChange={() => updateQ(q.id, { answer_index: oi })} data-testid={`q-${q.id}-opt-${oi}`} />
                            <span className="text-xs text-slate-400 w-4">{letters[oi]})</span>
                            <Input value={opt} onChange={(e) => { const o = [...q.options]; o[oi] = e.target.value; updateQ(q.id, { options: o }); }} className="h-8 rounded-lg text-sm" />
                            {hasMath(wrapBareMath(opt)) && <MathInline text={opt} className="text-xs text-slate-600 dark:text-slate-300 shrink-0" />}
                          </div>
                        ))}
                        <div className="text-[11px] text-slate-400">Select the radio for the correct option.</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-slate-200 dark:border-slate-700 pt-4 mt-2 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Send to Section(s)</Label>
              <div className="flex flex-wrap gap-2 mt-1.5" data-testid="paper-sections">
                {sections.length > 1 && (() => {
                  const allOn = selectedSections.length === sections.length;
                  return (
                    <label
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm cursor-pointer font-medium transition-colors ${allOn ? "border-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-200" : "border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:border-emerald-300"}`}
                      data-testid="paper-section-all"
                    >
                      <input
                        type="checkbox"
                        checked={allOn}
                        onChange={() => setSelectedSections(allOn ? [sections[0]] : [...sections])}
                      />
                      All Sections – {classLabel(classFilter)}
                    </label>
                  );
                })()}
                {sections.map((s) => {
                  const on = selectedSections.includes(s);
                  return (
                    <label key={s} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm cursor-pointer transition-colors ${on ? "border-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-200" : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-indigo-300"}`} data-testid={`paper-section-cb-${s}`}>
                      <input type="checkbox" checked={on} onChange={() => setSelectedSections((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s])} />
                      Section {s}
                    </label>
                  );
                })}
              </div>
              {sections.length > 1 && (
                <div className="text-[11px] text-slate-400 mt-1">As class teacher of {classLabel(classFilter)}, you can assign to any or all its sections.</div>
              )}
            </div>
            <div>
              <Label className="text-xs">Due date &amp; time</Label>
              <Input type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} className="h-10 rounded-xl mt-1" data-testid="paper-due" />
            </div>
          </div>

          <div>
            {hasCategories ? (
              <div className="text-sm text-slate-600 dark:text-slate-300" data-testid="paper-category-note">
                Sending to <span className="font-semibold text-slate-800 dark:text-slate-100">{pickedStudents.length}</span> student(s) in {categories.map((c) => TIER_LABEL[c]).join(", ")} across the selected section(s). Untick anyone below to exclude them.
              </div>
            ) : (
              <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                <input type="checkbox" checked={specific} onChange={(e) => setSpecific(e.target.checked)} data-testid="paper-specific-toggle" />
                Send only to specific students (otherwise the whole section)
              </label>
            )}
            {specific && (
              <div className="grid sm:grid-cols-2 gap-2 mt-2 max-h-40 overflow-y-auto" data-testid="paper-roster">
                {roster.length === 0 ? <div className="text-sm text-slate-400">No students in the selected section(s).</div> :
                  roster.map((st) => (
                    <label key={st.id} className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={pickedStudents.includes(st.id)} onChange={() => setPickedStudents((p) => p.includes(st.id) ? p.filter((x) => x !== st.id) : [...p, st.id])} />
                      {st.name} <span className="text-slate-400">({st.roll_number}{st._section ? ` · ${st._section}` : ""})</span>
                    </label>
                  ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-full" data-testid="paper-cancel">Cancel</Button>
          <Button variant="outline" onClick={openPreview} className="rounded-full" data-testid="paper-preview-btn">
            <FileText className="h-4 w-4 mr-2" /> Preview
          </Button>
          <Button variant="outline" onClick={downloadWord} className="rounded-full" data-testid="paper-word">
            <FileText className="h-4 w-4 mr-2" /> Download Word
          </Button>
          <Button variant="outline" onClick={savePaper} disabled={saving} className="rounded-full" data-testid="paper-save">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Save paper
          </Button>
          <span
            title={isFormalExam ? "Formal exams cannot be sent to students. Download the PDF/Word paper instead." : undefined}
            data-testid="paper-send-wrap"
          >
            <Button onClick={send} disabled={sending || isFormalExam} className="rounded-full bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="paper-send">
              {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />} Send to students
            </Button>
          </span>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={!!previewHtml} onOpenChange={(o) => !o && setPreviewHtml(null)}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-auto bg-slate-100 p-4" data-testid="paper-preview-dialog">
        <DialogHeader>
          <DialogTitle>Question Paper Preview</DialogTitle>
          <DialogDescription>This is exactly how the downloaded PDF will look.</DialogDescription>
        </DialogHeader>
        <div className="flex justify-center overflow-auto">
          <div
            className="qp-root shadow-lg"
            style={{ transformOrigin: "top center" }}
            dir={rtl ? "rtl" : "ltr"}
            dangerouslySetInnerHTML={{ __html: previewHtml || "" }}
          />
        </div>
      </DialogContent>
    </Dialog>

    <FigureEditor
      open={!!figEditQ}
      initialShapes={(questions.find((q) => q.id === figEditQ) || {}).figure_shapes}
      onClose={() => setFigEditQ(null)}
      onSave={(svg, shapes) => { updateQ(figEditQ, { figure_svg: svg, figure_shapes: shapes }); setFigEditQ(null); toast.success(svg ? "Figure added" : "Figure cleared"); }}
    />

    <Dialog open={!!sketchPickerQ} onOpenChange={(o) => !o && setSketchPickerQ(null)}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="saved-sketch-picker">
        <DialogHeader>
          <DialogTitle>Insert a saved sketch</DialogTitle>
          <DialogDescription>Pick a sketch you saved from the AI mentor (Teacher Guidance → My Notes). It will be added as this question's figure.</DialogDescription>
        </DialogHeader>
        {savedSketches === null ? (
          <div className="py-10 flex items-center justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading your sketches…</div>
        ) : savedSketches.length === 0 ? (
          <div className="py-10 text-center text-sm text-slate-500" data-testid="saved-sketch-empty">
            No saved sketches yet. Open <span className="font-semibold">Teacher Guidance</span>, ask the AI mentor for a diagram, then tap <span className="font-semibold">"Save to My Notes"</span> under the sketch.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {savedSketches.map((n) => (
              <button
                key={n.id}
                onClick={() => { updateQ(sketchPickerQ, { figure_svg: n.svg, figure_shapes: undefined }); setSketchPickerQ(null); toast.success("Sketch inserted"); }}
                className="group text-left rounded-xl border border-slate-200 dark:border-slate-700 p-2 hover:border-amber-400 hover:shadow-sm transition-colors bg-white"
                data-testid={`saved-sketch-item-${n.id}`}
              >
                <div className="aspect-video flex items-center justify-center overflow-hidden rounded-lg bg-slate-50 [&_svg]:max-w-full [&_svg]:max-h-full [&_svg]:w-auto [&_svg]:h-auto" dangerouslySetInnerHTML={{ __html: n.svg }} />
                <div className="mt-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 truncate">{n.lesson_title || n.subject || "Saved sketch"}</div>
                {(n.subject || n.class_name) && (
                  <div className="text-[10px] text-slate-400 truncate">{[n.subject, n.class_name].filter(Boolean).join(" · ")}</div>
                )}
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}
function ExamResults({ examId, onBack }) {
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [reminding, setReminding] = useState(false);
  const [grading, setGrading] = useState(null); // submission being graded

  const load = async () => {
    try {
      const [subsRes, anRes] = await Promise.all([
        api.get(`/teacher/exams/${examId}/submissions`),
        api.get(`/teacher/exams/${examId}/analytics`),
      ]);
      setData(subsRes.data);
      setAnalytics(anRes.data);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Could not load submissions"); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [examId]);

  const remind = () => {
    toast.success("WhatsApp reminder coming soon!");
  };

  if (!data) return <Layout><div className="max-w-4xl mx-auto text-slate-500">Loading…</div></Layout>;
  const { exam, submissions } = data;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto" data-testid="exam-results">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" className="rounded-full" onClick={onBack} data-testid="results-back"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>{exam.title}</h1>
            <div className="text-xs text-slate-500">{classLabel(exam.class_name)} · Sec {exam.section} · {exam.total_marks} marks · Due {fmtDate(exam.due_at)}</div>
          </div>
        </div>

        {analytics && (
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 mb-5" data-testid="exam-analytics">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Stat label="Submitted" value={`${analytics.submitted}/${analytics.assigned}`} />
              <Stat label="Class average" value={analytics.average != null ? `${analytics.average}/${analytics.max_score}` : "—"} />
              <Stat label="Needs grading" value={analytics.needs_grading} />
              <Stat label="Not attempted" value={analytics.not_attempted.length} />
            </div>

            {analytics.per_question?.length > 0 && (
              <div className="mt-5">
                <div className="text-xs uppercase tracking-[0.15em] text-slate-400 mb-2">Per-question performance</div>
                <div className="space-y-2" data-testid="analytics-questions">
                  {analytics.per_question.map((q, i) => (
                    <div key={q.id} className="text-sm">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-slate-600 dark:text-slate-300 truncate">{i + 1}. {q.prompt}</span>
                        <span className="text-slate-400 shrink-0 text-xs">
                          {q.type === "mcq"
                            ? (q.correct_rate != null ? `${q.correct_rate}% correct` : "no responses")
                            : (q.avg_marks != null ? `avg ${q.avg_marks}/${q.marks}` : "ungraded")}
                        </span>
                      </div>
                      {q.type === "mcq" && q.correct_rate != null && (
                        <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-700 mt-1 overflow-hidden">
                          <div className="h-full bg-emerald-500" style={{ width: `${q.correct_rate}%` }} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {analytics.not_attempted.length > 0 && !exam.locked && (
              <div className="mt-5 flex items-center justify-between gap-3 flex-wrap border-t border-slate-100 dark:border-slate-700 pt-4">
                <div className="text-sm text-slate-600 dark:text-slate-300">
                  <span className="font-medium">{analytics.not_attempted.length}</span> student(s) haven't attempted:
                  <span className="text-slate-400"> {analytics.not_attempted.slice(0, 6).map((s) => s.name).join(", ")}{analytics.not_attempted.length > 6 ? "…" : ""}</span>
                </div>
                <Button size="sm" variant="outline" className="rounded-full border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800 dark:border-emerald-800 dark:text-emerald-400 dark:hover:bg-emerald-950" onClick={remind} data-testid="remind-btn">
                  <MessageCircle className="h-4 w-4 mr-2" /> Remind by WhatsApp
                </Button>
              </div>
            )}
          </div>
        )}

        {submissions.length === 0 ? (
          <div className="text-slate-500 text-sm" data-testid="results-empty">No submissions yet.</div>
        ) : (
          <div className="space-y-3" data-testid="submissions-list">
            {submissions.map((s) => (
              <div key={s.id} className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 flex items-center justify-between gap-3">
                <div>
                  <div className="font-medium text-slate-900 dark:text-slate-100">{s.student_name} <span className="text-slate-400 text-xs">({s.roll_number})</span></div>
                  <div className="text-xs text-slate-500 mt-0.5">Submitted {fmtDate(s.submitted_at)} · {s.image_ids?.length ? `${s.image_ids.length} photo(s)` : "online"}</div>
                </div>
                <div className="flex items-center gap-3">
                  {s.status === "graded"
                    ? <span className="text-sm font-semibold text-emerald-600" data-testid={`sub-score-${s.id}`}>{s.score}/{exam.total_marks}</span>
                    : <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-600">Needs grading</span>}
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => setGrading(s)} data-testid={`grade-btn-${s.id}`}>Review &amp; grade</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {grading && (
        <GradeDialog exam={exam} sub={grading} onClose={() => setGrading(null)} onGraded={() => { setGrading(null); load(); }} />
      )}
    </Layout>
  );
}

function GradeDialog({ exam, sub, onClose, onGraded }) {
  const qmap = Object.fromEntries(exam.questions.map((q) => [q.id, q]));
  const [subState, setSubState] = useState(sub);
  const ansMap = Object.fromEntries((subState.answers || []).map((a) => [a.question_id, a]));
  const [marks, setMarks] = useState(() => {
    const init = {};
    exam.questions.forEach((q) => {
      if (q.type === "mcq" || q.type === "blanks" || q.type === "match") init[q.id] = (sub.awarded || {})[q.id] ?? 0;
      else init[q.id] = (sub.awarded || {})[q.id] ?? "";
    });
    return init;
  });
  const [feedback, setFeedback] = useState(sub.feedback || "");
  const [saving, setSaving] = useState(false);
  const [recropping, setRecropping] = useState(false);
  const [zoom, setZoom] = useState(null);
  const [cropQ, setCropQ] = useState(null); // question whose crop box is being adjusted
  const cropStatus = subState.crop_status;
  const answerCrops = subState.answer_crops || {};
  const cropMeta = subState.answer_crop_meta || {};
  const sheets = (subState.image_ids || []).map((id) => ({ id, url: `${API_BASE}/exam-files/${id}` }));

  const saveCrop = async ({ image_id, box }) => {
    try {
      await api.post(`/teacher/exam-submissions/${sub.id}/recrop-question`, { question_id: cropQ, image_id, box });
      setCropQ(null);
      await refreshSub();
      toast.success("Crop updated");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Could not update the crop"); }
  };

  // While the AI crop runs in the background, poll for the finished crops.
  const refreshSub = async () => {
    try {
      const { data } = await api.get(`/teacher/exams/${exam.id}/submissions`);
      const fresh = (data.submissions || []).find((s) => s.id === sub.id);
      if (fresh) setSubState(fresh);
      return fresh;
    } catch { return null; }
  };

  useEffect(() => {
    if (cropStatus !== "processing") return;
    const t = setInterval(refreshSub, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cropStatus]);

  const rerunCrop = async () => {
    setRecropping(true);
    try {
      await api.post(`/teacher/exam-submissions/${sub.id}/autocrop`);
      setSubState((s) => ({ ...s, crop_status: "processing", answer_crops: {} }));
      toast.message("Re-cropping answers… this takes a few seconds.");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not re-run auto-crop");
    } finally { setRecropping(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(marks).forEach(([k, v]) => { if (v !== "" && v != null) payload[k] = Number(v); });
      await api.patch(`/teacher/exam-submissions/${sub.id}/grade`, { marks: payload, feedback });
      toast.success("Marks saved.");
      onGraded();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not save marks");
    } finally { setSaving(false); }
  };

  const hasSheets = subState.image_ids?.length > 0;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="grade-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }}>{subState.student_name} · {subState.roll_number}</DialogTitle>
          <DialogDescription>Review answers and award marks. MCQs are pre-filled from auto-grading.</DialogDescription>
        </DialogHeader>

        {hasSheets && (
          <div className="space-y-2" data-testid="grade-images">
            <div className="flex items-center justify-between gap-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">Uploaded answer sheets</Label>
              <div className="flex items-center gap-2">
                {cropStatus === "processing" && (
                  <span className="text-[11px] text-sky-600 flex items-center gap-1" data-testid="crop-processing"><Loader2 className="h-3 w-3 animate-spin" /> Auto-cropping answers…</span>
                )}
                <button onClick={rerunCrop} disabled={recropping || cropStatus === "processing"} className="text-[11px] text-slate-500 hover:text-sky-600 flex items-center gap-1 disabled:opacity-40" title="Auto-crop each answer under its question" data-testid="crop-rerun">
                  <Scissors className="h-3 w-3" /> Auto-crop answers
                </button>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {subState.image_ids.map((fid) => (
                <button key={fid} type="button" onClick={() => setZoom(`${API_BASE}/exam-files/${fid}`)} className="block rounded-lg overflow-hidden focus:outline-none focus:ring-2 focus:ring-sky-400" data-testid={`grade-sheet-${fid}`}>
                  <img src={`${API_BASE}/exam-files/${fid}`} alt="answer sheet" className="h-28 w-auto rounded-lg border border-slate-200 dark:border-slate-700 hover:opacity-90 transition-opacity" />
                </button>
              ))}
            </div>
            {cropStatus === "empty" && (
              <div className="text-[11px] text-amber-600" data-testid="crop-empty">Couldn't detect answers automatically — review the full sheet(s) above, or try "Auto-crop answers" again.</div>
            )}
            {cropStatus === "failed" && (
              <div className="text-[11px] text-rose-500" data-testid="crop-failed">Auto-crop failed. You can retry, or grade using the full sheet(s) above.</div>
            )}
          </div>
        )}

        <div className="space-y-3 mt-2">
          {exam.questions.map((q, i) => {
            const a = ansMap[q.id] || {};
            const cropId = answerCrops[q.id];
            return (
              <div key={q.id} className="rounded-xl border border-slate-200 dark:border-slate-700 p-3">
                <div className="text-sm font-medium text-slate-800 dark:text-slate-100">{i + 1}. <MathInline text={q.prompt} /> <span className="text-xs text-slate-400">({q.marks} marks · {q.type})</span></div>
                {q.figure_svg && (
                  <div className="mt-2 inline-block rounded-lg border border-slate-200 dark:border-slate-700 bg-white p-2 [&_svg]:max-w-[280px] [&_svg]:h-auto" dangerouslySetInnerHTML={{ __html: q.figure_svg }} />
                )}
                {q.figure_image && (
                  <img src={`${API_BASE}${q.figure_image}`} alt="attached" className="mt-2 max-w-[280px] max-h-[200px] rounded-lg border border-slate-200 dark:border-slate-700 bg-white" />
                )}
                {q.type === "mcq" ? (
                  <div className="text-sm mt-1">
                    <span className={a.selected === q.answer_index ? "text-emerald-600" : "text-rose-500"}>
                      Chose: {a.selected >= 0 ? <MathInline text={q.options[a.selected]} /> : "—"}
                    </span>
                    <span className="text-slate-400"> · Correct: <MathInline text={q.options[q.answer_index]} /></span>
                  </div>
                ) : q.type === "blanks" ? (
                  <div className="text-sm mt-1 space-y-1" data-testid={`grade-blanks-${q.id}`}>
                    {(q.blanks || []).map((b, bi) => {
                      const picked = (a.selected_blanks || [])[bi];
                      const ok = picked === b.correct_index;
                      return (
                        <div key={bi} className="flex flex-wrap items-center gap-1.5">
                          {(q.blanks || []).length > 1 && <span className="text-slate-400 text-xs">B{bi + 1}:</span>}
                          <span className={ok ? "text-emerald-600" : "text-rose-500"}>
                            Chose: {picked != null && picked >= 0 ? (b.options || [])[picked] : "—"}
                          </span>
                          <span className="text-slate-400">· Correct: {(b.options || [])[b.correct_index]}</span>
                        </div>
                      );
                    })}
                    <div className="text-[11px] text-slate-400">Auto-graded (all-or-nothing).</div>
                  </div>
                ) : q.type === "match" ? (
                  <div className="text-sm mt-1 space-y-1" data-testid={`grade-match-${q.id}`}>
                    {(q.pairs || []).map((p, pi) => {
                      const ropts = q.right_options || [];
                      const idx = (a.selected_matches || [])[pi];
                      const chose = (idx != null && idx >= 0 && idx < ropts.length) ? ropts[idx] : "—";
                      const ok = chose === p.right;
                      return (
                        <div key={pi} className="flex flex-wrap items-center gap-1.5">
                          <span className="text-slate-500">{pi + 1}. {p.left} →</span>
                          <span className={ok ? "text-emerald-600" : "text-rose-500"}>{chose}</span>
                          {!ok && <span className="text-slate-400">· Correct: {p.right}</span>}
                        </div>
                      );
                    })}
                    <div className="text-[11px] text-slate-400">Auto-graded · 1 mark per correct pair.</div>
                  </div>
                ) : q.type === "poem" ? (
                  <div className="text-sm mt-1" data-testid={`grade-poem-${q.id}`}>
                    <div className="text-slate-500 whitespace-pre-wrap bg-slate-50 dark:bg-slate-900/40 rounded-lg p-2 text-xs">{(q.poem || "").replace(/_{2,}/g, "＿＿＿")}</div>
                    {a.text?.trim() && <div className="mt-1 text-slate-600 dark:text-slate-300 whitespace-pre-wrap bg-slate-50 dark:bg-slate-900/40 rounded-lg p-2">{a.text}</div>}
                    <div className="text-[11px] text-slate-400 mt-1">Print-only — grade from the uploaded sheet / typed answer.</div>
                  </div>
                ) : (
                  <>
                    {a.text?.trim() && (
                      <div className="text-sm mt-1 text-slate-600 dark:text-slate-300 whitespace-pre-wrap bg-slate-50 dark:bg-slate-900/40 rounded-lg p-2">{a.text}</div>
                    )}
                    {cropId ? (
                      <div className="mt-2" data-testid={`grade-crop-${q.id}`}>
                        <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1 flex items-center justify-between gap-2">
                          <span className="flex items-center gap-1"><Scissors className="h-3 w-3" /> Cropped answer</span>
                          {sheets.length > 0 && (
                            <button type="button" onClick={() => setCropQ(q.id)} className="text-sky-600 hover:text-sky-700 normal-case tracking-normal font-medium" data-testid={`crop-adjust-${q.id}`}>Adjust crop</button>
                          )}
                        </div>
                        <button type="button" onClick={() => setZoom(`${API_BASE}/exam-files/${cropId}`)} className="block rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-400">
                          <img src={`${API_BASE}/exam-files/${cropId}`} alt="cropped answer" className="max-w-full max-h-72 rounded-lg border border-slate-200 dark:border-slate-700 bg-white hover:opacity-90 transition-opacity" />
                        </button>
                      </div>
                    ) : (!a.text?.trim() && (
                      <div className="text-sm mt-1 text-slate-400 italic flex items-center gap-2 flex-wrap">
                        <span>{cropStatus === "processing" ? "Cropping this answer…" : "No typed answer (see uploaded sheet above)."}</span>
                        {sheets.length > 0 && cropStatus !== "processing" && (
                          <button type="button" onClick={() => setCropQ(q.id)} className="not-italic text-sky-600 hover:text-sky-700 font-medium" data-testid={`crop-adjust-${q.id}`}>Crop an answer</button>
                        )}
                      </div>
                    ))}
                  </>
                )}
                <div className="flex items-center gap-2 mt-2">
                  <Label className="text-xs">Marks</Label>
                  <Input type="number" min={0} max={q.marks} value={marks[q.id]} onChange={(e) => setMarks((m) => ({ ...m, [q.id]: e.target.value }))} className="h-8 w-20 rounded-lg" data-testid={`grade-marks-${q.id}`} />
                  <span className="text-xs text-slate-400">/ {q.marks}</span>
                </div>
              </div>
            );
          })}
        </div>

        <div>
          <Label className="text-xs">Feedback (optional)</Label>
          <Textarea value={feedback} onChange={(e) => setFeedback(e.target.value)} className="rounded-xl mt-1" data-testid="grade-feedback" />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-full bg-indigo-500 hover:bg-indigo-600" data-testid="grade-save">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />} Save marks
          </Button>
        </DialogFooter>
      </DialogContent>

      <Dialog open={!!zoom} onOpenChange={(o) => !o && setZoom(null)}>
        <DialogContent className="max-w-4xl rounded-2xl p-2 bg-white dark:bg-slate-900" data-testid="grade-image-lightbox">
          <DialogHeader className="sr-only">
            <DialogTitle>Answer sheet</DialogTitle>
            <DialogDescription>Enlarged answer image</DialogDescription>
          </DialogHeader>
          {zoom && <img src={zoom} alt="answer" className="w-full h-auto max-h-[82vh] object-contain rounded-lg" />}
        </DialogContent>
      </Dialog>

      <CropAdjuster
        open={!!cropQ}
        sheets={sheets}
        initial={cropQ ? cropMeta[cropQ] : null}
        onClose={() => setCropQ(null)}
        onSave={saveCrop}
      />
    </Dialog>
  );
}
